// ============================================================
// JOB TEMPLATES — Ships with app (local data, no Supabase)
// Same pattern as projectTemplates.ts and scheduleConfig.ts
// ============================================================

// ============================================================
// TYPES
// ============================================================

export interface JobTemplateLineItem {
  description: string;
  type: 'labour' | 'material' | 'flat_rate';
  defaultAmount: number; // CAD
  unit?: string; // 'hour', 'each', 'sqft', 'km', etc.
  quantity?: number;
}

export interface JobTemplate {
  id: string; // kebab-case: 'plumbing-clogged-drain'
  name: string;
  description: string; // default job description when selected
  industryId: string; // matches categoryId from industryTaxonomy.ts
  estimatedMinutes: number;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  checklist: string[]; // default checklist items
  lineItems: JobTemplateLineItem[];
  tags: string[]; // for fuzzy search
}

// ============================================================
// PLUMBING & HVAC  (industryId: 'home_services')
// ============================================================

const plumbingHvacTemplates: JobTemplate[] = [
  {
    id: 'plumbing-clogged-drain',
    name: 'Clogged Drain',
    description: 'Clear blocked drain — kitchen, bathroom, or floor drain.',
    industryId: 'home_services',
    estimatedMinutes: 60,
    priority: 'medium',
    checklist: [
      'Assess blockage location',
      'Camera inspect if needed',
      'Snake/auger drain',
      'Flush and test flow',
      'Clean work area',
      'Advise on prevention',
    ],
    lineItems: [
      { description: 'Service call', type: 'flat_rate', defaultAmount: 150 },
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour', quantity: 1 },
      { description: 'Materials if needed', type: 'material', defaultAmount: 0 },
    ],
    tags: ['drain', 'clog', 'blocked', 'slow', 'backup', 'sink', 'shower', 'tub'],
  },
  {
    id: 'plumbing-burst-pipe',
    name: 'Burst Pipe / Emergency Leak',
    description: 'Emergency water leak or burst pipe — shut off, repair, restore.',
    industryId: 'home_services',
    estimatedMinutes: 120,
    priority: 'urgent',
    checklist: [
      'Locate and shut off water supply',
      'Assess pipe damage',
      'Temporary patch if needed',
      'Cut and replace damaged section',
      'Pressure test repair',
      'Check for water damage',
      'Restore water supply',
      'Document damage for insurance',
    ],
    lineItems: [
      { description: 'Emergency service call', type: 'flat_rate', defaultAmount: 250 },
      { description: 'Labour', type: 'labour', defaultAmount: 95, unit: 'hour', quantity: 2 },
      { description: 'Pipe fittings', type: 'material', defaultAmount: 45 },
      { description: 'Cleanup', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['burst', 'pipe', 'leak', 'emergency', 'flood', 'water', 'broken'],
  },
  {
    id: 'plumbing-water-heater-install',
    name: 'Water Heater Install',
    description: 'Remove old water heater, install new unit, test and commission.',
    industryId: 'home_services',
    estimatedMinutes: 240,
    priority: 'medium',
    checklist: [
      'Shut off water and gas/power',
      'Drain old tank',
      'Disconnect and remove old unit',
      'Position new unit',
      'Connect water lines',
      'Connect gas line or electrical',
      'Install T&P relief valve and discharge pipe',
      'Fill and test for leaks',
      'Light pilot / power on',
      'Set temperature',
      'Verify hot water at fixtures',
      'Remove old unit from property',
    ],
    lineItems: [
      { description: 'Installation labour', type: 'labour', defaultAmount: 95, unit: 'hour', quantity: 4 },
      { description: 'Water heater unit', type: 'material', defaultAmount: 0 },
      { description: 'Fittings and connectors', type: 'material', defaultAmount: 65 },
      { description: 'Disposal of old tank', type: 'flat_rate', defaultAmount: 75 },
    ],
    tags: ['water heater', 'hot water', 'tank', 'install', 'replace', 'gas', 'electric'],
  },
  {
    id: 'plumbing-water-heater-repair',
    name: 'Water Heater Repair',
    description: 'Diagnose and repair water heater issue — no hot water, leaking, strange noises.',
    industryId: 'home_services',
    estimatedMinutes: 90,
    priority: 'high',
    checklist: [
      'Inspect unit and identify issue',
      'Check pilot light / heating element',
      'Check thermostat',
      'Check T&P valve',
      'Test gas line (if gas)',
      'Replace faulty component',
      'Test operation',
      'Verify temperature at fixtures',
    ],
    lineItems: [
      { description: 'Service call', type: 'flat_rate', defaultAmount: 150 },
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour', quantity: 1 },
      { description: 'Parts', type: 'material', defaultAmount: 0 },
    ],
    tags: ['water heater', 'repair', 'no hot water', 'leaking', 'pilot', 'thermostat'],
  },
  {
    id: 'plumbing-fixture-replacement',
    name: 'Fixture Replacement',
    description: 'Replace faucet, toilet, or other plumbing fixture.',
    industryId: 'home_services',
    estimatedMinutes: 90,
    priority: 'low',
    checklist: [
      'Shut off water supply',
      'Remove old fixture',
      'Clean mounting surface',
      'Install new fixture',
      'Connect supply lines',
      'Test for leaks',
      'Test operation',
      'Clean up',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour', quantity: 1.5 },
      { description: 'Fixture', type: 'material', defaultAmount: 0 },
      { description: 'Supply lines and fittings', type: 'material', defaultAmount: 25 },
    ],
    tags: ['faucet', 'toilet', 'fixture', 'replace', 'install', 'sink', 'bidet', 'showerhead'],
  },
  {
    id: 'plumbing-leak-detection',
    name: 'Leak Detection',
    description: 'Locate source of water leak — visible or hidden.',
    industryId: 'home_services',
    estimatedMinutes: 90,
    priority: 'high',
    checklist: [
      'Interview client on symptoms',
      'Visual inspection of accessible plumbing',
      'Check water meter for movement',
      'Pressure test system',
      'Camera inspect if needed',
      'Mark leak location',
      'Provide repair estimate',
      'Document findings with photos',
    ],
    lineItems: [
      { description: 'Leak detection fee', type: 'flat_rate', defaultAmount: 200 },
      { description: 'Camera inspection', type: 'flat_rate', defaultAmount: 150 },
    ],
    tags: ['leak', 'detection', 'hidden', 'water damage', 'moisture', 'damp'],
  },
  {
    id: 'plumbing-sewer-line',
    name: 'Sewer Line Service',
    description: 'Clear sewer line blockage or perform sewer inspection.',
    industryId: 'home_services',
    estimatedMinutes: 120,
    priority: 'high',
    checklist: [
      'Locate cleanout access',
      'Camera inspect sewer line',
      'Identify blockage or damage',
      'Power auger / hydro jet',
      'Re-inspect after clearing',
      'Document findings',
      'Advise on repairs if needed',
    ],
    lineItems: [
      { description: 'Sewer service call', type: 'flat_rate', defaultAmount: 250 },
      { description: 'Camera inspection', type: 'flat_rate', defaultAmount: 175 },
      { description: 'Hydro jetting', type: 'flat_rate', defaultAmount: 350 },
    ],
    tags: ['sewer', 'mainline', 'blockage', 'tree roots', 'backup', 'cleanout'],
  },
  {
    id: 'plumbing-backflow-testing',
    name: 'Backflow Testing',
    description: 'Annual backflow preventer test and certification.',
    industryId: 'home_services',
    estimatedMinutes: 45,
    priority: 'low',
    checklist: [
      'Locate backflow device',
      'Visual inspection of device',
      'Perform test with certified gauge',
      'Record test results',
      'Submit report to municipality',
      'Provide certificate to client',
      'Tag device with test date',
    ],
    lineItems: [
      { description: 'Backflow test', type: 'flat_rate', defaultAmount: 125 },
    ],
    tags: ['backflow', 'test', 'certification', 'annual', 'cross-connection'],
  },
  {
    id: 'hvac-furnace-maintenance',
    name: 'Furnace Maintenance',
    description: 'Annual furnace tune-up and safety inspection.',
    industryId: 'home_services',
    estimatedMinutes: 90,
    priority: 'low',
    checklist: [
      'Inspect and replace air filter',
      'Clean burners',
      'Check heat exchanger for cracks',
      'Test ignition system',
      'Check gas pressure',
      'Inspect flue and venting',
      'Test thermostat operation',
      'Check carbon monoxide levels',
      'Lubricate moving parts',
      'Test safety controls',
      'Record findings',
    ],
    lineItems: [
      { description: 'Furnace tune-up', type: 'flat_rate', defaultAmount: 175 },
      { description: 'Filter', type: 'material', defaultAmount: 25 },
    ],
    tags: ['furnace', 'maintenance', 'tune-up', 'heating', 'filter', 'annual'],
  },
  {
    id: 'hvac-ac-service',
    name: 'AC Service',
    description: 'Air conditioning service, repair, or seasonal start-up.',
    industryId: 'home_services',
    estimatedMinutes: 90,
    priority: 'medium',
    checklist: [
      'Inspect outdoor condenser unit',
      'Clean condenser coils',
      'Check refrigerant levels',
      'Inspect indoor evaporator coil',
      'Check drainage',
      'Test thermostat and controls',
      'Inspect electrical connections',
      'Check airflow at registers',
      'Record findings',
    ],
    lineItems: [
      { description: 'AC service call', type: 'flat_rate', defaultAmount: 175 },
      { description: 'Refrigerant top-up', type: 'material', defaultAmount: 0 },
    ],
    tags: ['AC', 'air conditioning', 'cooling', 'refrigerant', 'condenser', 'compressor'],
  },
  {
    id: 'hvac-gas-line',
    name: 'Gas Line Work',
    description: 'Gas line install, repair, or leak investigation.',
    industryId: 'home_services',
    estimatedMinutes: 120,
    priority: 'urgent',
    checklist: [
      'Gas leak detection (soap test / electronic detector)',
      'Shut off gas if needed',
      'Repair or install gas line',
      'Pressure test new/repaired line',
      'Check all connections',
      'Test appliance operation',
      'Verify no leaks on departure',
      'Provide gas safety certificate if required',
    ],
    lineItems: [
      { description: 'Gas line service', type: 'flat_rate', defaultAmount: 225 },
      { description: 'Labour', type: 'labour', defaultAmount: 95, unit: 'hour', quantity: 2 },
      { description: 'Fittings', type: 'material', defaultAmount: 40 },
    ],
    tags: ['gas', 'line', 'leak', 'install', 'repair', 'natural gas', 'propane', 'BBQ'],
  },
  {
    id: 'plumbing-sump-pump',
    name: 'Sump Pump Install/Repair',
    description: 'Install new or repair existing sump pump system.',
    industryId: 'home_services',
    estimatedMinutes: 120,
    priority: 'high',
    checklist: [
      'Inspect sump pit',
      'Test existing pump (if repair)',
      'Remove old pump (if replacement)',
      'Install new pump',
      'Connect discharge line',
      'Test float switch',
      'Test backup battery (if equipped)',
      'Verify discharge location',
      'Clean up',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour', quantity: 2 },
      { description: 'Sump pump', type: 'material', defaultAmount: 0 },
      { description: 'Check valve and fittings', type: 'material', defaultAmount: 35 },
    ],
    tags: ['sump pump', 'basement', 'flooding', 'backup', 'drainage'],
  },
];

// ============================================================
// ELECTRICAL  (industryId: 'home_services')
// ============================================================

const electricalTemplates: JobTemplate[] = [
  {
    id: 'electrical-panel-upgrade',
    name: 'Panel Upgrade',
    description: 'Upgrade electrical panel to higher amperage or replace outdated panel.',
    industryId: 'home_services',
    estimatedMinutes: 480,
    priority: 'medium',
    checklist: [
      'De-energize panel',
      'Remove panel cover',
      'Label and photograph existing wiring',
      'Disconnect all circuits',
      'Remove old panel',
      'Mount new panel',
      'Reconnect all circuits per code',
      'Install new breakers',
      'Ground and bond per code',
      'Energize and test all circuits',
      'Update panel directory',
      'ESA inspection notification',
      'Final cleanup',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 95, unit: 'hour', quantity: 8 },
      { description: 'Panel and breakers', type: 'material', defaultAmount: 0 },
      { description: 'Permit', type: 'flat_rate', defaultAmount: 150 },
      { description: 'ESA inspection fee', type: 'flat_rate', defaultAmount: 125 },
    ],
    tags: ['panel', 'upgrade', '100amp', '200amp', 'breaker', 'fuse', 'electrical panel'],
  },
  {
    id: 'electrical-outlet-install',
    name: 'Outlet Install/Replace',
    description: 'Install new outlet or replace existing — standard, GFCI, or USB.',
    industryId: 'home_services',
    estimatedMinutes: 45,
    priority: 'low',
    checklist: [
      'Shut off circuit breaker',
      'Verify power off with tester',
      'Remove old outlet (if replacing)',
      'Run wire if new location',
      'Wire new outlet',
      'Install cover plate',
      'Restore power',
      'Test with plug tester',
      'Test GFCI trip/reset (if applicable)',
    ],
    lineItems: [
      { description: 'Service call', type: 'flat_rate', defaultAmount: 125 },
      { description: 'Labour per outlet', type: 'labour', defaultAmount: 45, unit: 'each' },
      { description: 'Outlet and cover plate', type: 'material', defaultAmount: 15 },
    ],
    tags: ['outlet', 'receptacle', 'plug', 'GFCI', 'USB', 'bathroom', 'kitchen', 'outdoor'],
  },
  {
    id: 'electrical-light-fixture',
    name: 'Light Fixture Install',
    description: 'Install or replace light fixture — interior or exterior.',
    industryId: 'home_services',
    estimatedMinutes: 60,
    priority: 'low',
    checklist: [
      'Shut off circuit',
      'Remove old fixture',
      'Check junction box and wiring',
      'Mount new fixture bracket',
      'Connect wires (black-black, white-white, ground)',
      'Secure fixture',
      'Install bulbs',
      'Restore power and test',
      'Test switch operation',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour', quantity: 1 },
      { description: 'Fixture mounting hardware', type: 'material', defaultAmount: 10 },
    ],
    tags: ['light', 'fixture', 'chandelier', 'pendant', 'pot light', 'recessed', 'exterior', 'install'],
  },
  {
    id: 'electrical-ceiling-fan',
    name: 'Ceiling Fan Install',
    description: 'Install ceiling fan — with or without existing wiring.',
    industryId: 'home_services',
    estimatedMinutes: 90,
    priority: 'low',
    checklist: [
      'Shut off circuit',
      'Check ceiling box rated for fan weight',
      'Install fan-rated box if needed',
      'Assemble fan components',
      'Mount bracket',
      'Wire connections',
      'Attach fan blades',
      'Install light kit (if applicable)',
      'Balance fan',
      'Test all speeds and light',
      'Program remote (if applicable)',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour', quantity: 1.5 },
      { description: 'Fan-rated box', type: 'material', defaultAmount: 25 },
    ],
    tags: ['ceiling fan', 'fan', 'install', 'bedroom', 'living room'],
  },
  {
    id: 'electrical-ev-charger',
    name: 'EV Charger Install',
    description: 'Install Level 2 electric vehicle charger — 240V dedicated circuit.',
    industryId: 'home_services',
    estimatedMinutes: 180,
    priority: 'medium',
    checklist: [
      'Assess panel capacity',
      'Plan circuit route',
      'Install dedicated 40A/50A breaker',
      'Run cable to charger location',
      'Mount charger unit',
      'Connect wiring',
      'Program charger',
      'Test charging with vehicle',
      'Provide ESA notification if required',
      'Clean up',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 95, unit: 'hour', quantity: 3 },
      { description: 'Wire and conduit', type: 'material', defaultAmount: 150 },
      { description: 'Breaker', type: 'material', defaultAmount: 45 },
      { description: 'Permit', type: 'flat_rate', defaultAmount: 100 },
    ],
    tags: ['EV', 'charger', 'electric vehicle', 'Tesla', 'Level 2', '240V', 'garage'],
  },
  {
    id: 'electrical-smoke-co-detector',
    name: 'Smoke/CO Detector Install',
    description: 'Install or replace smoke detectors and carbon monoxide detectors.',
    industryId: 'home_services',
    estimatedMinutes: 60,
    priority: 'medium',
    checklist: [
      'Assess current detector locations',
      'Check code requirements per room',
      'Remove old detectors',
      'Install new detectors (hardwired or battery)',
      'Test each detector',
      'Interconnect if hardwired',
      'Label with install date',
      'Advise client on maintenance',
    ],
    lineItems: [
      { description: 'Service call', type: 'flat_rate', defaultAmount: 125 },
      { description: 'Labour per detector', type: 'labour', defaultAmount: 25, unit: 'each' },
      { description: 'Detector', type: 'material', defaultAmount: 35, unit: 'each' },
    ],
    tags: ['smoke', 'detector', 'CO', 'carbon monoxide', 'alarm', 'safety', 'fire'],
  },
  {
    id: 'electrical-inspection',
    name: 'Electrical Inspection',
    description: 'Full electrical inspection — pre-purchase, insurance, or annual safety.',
    industryId: 'home_services',
    estimatedMinutes: 120,
    priority: 'low',
    checklist: [
      'Inspect main panel and breakers',
      'Check grounding and bonding',
      'Inspect all accessible wiring',
      'Test outlets with plug tester',
      'Check GFCI and AFCI protection',
      'Inspect light fixtures and switches',
      'Check exterior wiring and weatherheads',
      'Test smoke and CO detectors',
      'Note code violations',
      'Prepare written report',
    ],
    lineItems: [
      { description: 'Electrical inspection', type: 'flat_rate', defaultAmount: 300 },
    ],
    tags: ['inspection', 'safety', 'pre-purchase', 'insurance', 'code', 'report'],
  },
  {
    id: 'electrical-wiring-repair',
    name: 'Wiring Repair',
    description: 'Diagnose and repair electrical wiring issue — tripping breakers, flickering lights, dead circuits.',
    industryId: 'home_services',
    estimatedMinutes: 90,
    priority: 'high',
    checklist: [
      'Interview client on symptoms',
      'Test circuit at panel',
      'Trace wiring to locate fault',
      'Repair or replace damaged wiring',
      'Test repair',
      'Verify no other issues on circuit',
      'Restore power',
      'Document repair',
    ],
    lineItems: [
      { description: 'Diagnostic fee', type: 'flat_rate', defaultAmount: 150 },
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour', quantity: 1 },
      { description: 'Materials', type: 'material', defaultAmount: 0 },
    ],
    tags: ['wiring', 'repair', 'flickering', 'tripping', 'breaker', 'dead', 'outlet', 'short'],
  },
  {
    id: 'electrical-generator-install',
    name: 'Generator Install',
    description: 'Install backup generator — portable hookup or standby unit.',
    industryId: 'home_services',
    estimatedMinutes: 360,
    priority: 'low',
    checklist: [
      'Assess power requirements',
      'Install transfer switch at panel',
      'Run cable to generator location',
      'Mount generator pad (if standby)',
      'Connect fuel line (if natural gas)',
      'Wire transfer switch',
      'Program automatic start (if standby)',
      'Test under load',
      'Walk client through operation',
      'Provide maintenance schedule',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 95, unit: 'hour', quantity: 6 },
      { description: 'Transfer switch', type: 'material', defaultAmount: 350 },
      { description: 'Conduit and wire', type: 'material', defaultAmount: 200 },
      { description: 'Permit', type: 'flat_rate', defaultAmount: 150 },
    ],
    tags: ['generator', 'backup', 'standby', 'transfer switch', 'power outage'],
  },
  {
    id: 'electrical-landscape-lighting',
    name: 'Landscape Lighting',
    description: 'Install outdoor landscape lighting — pathway, accent, or security.',
    industryId: 'home_services',
    estimatedMinutes: 180,
    priority: 'low',
    checklist: [
      'Plan fixture locations with client',
      'Run low-voltage cable',
      'Install transformer',
      'Mount fixtures',
      'Connect wiring',
      'Set timer/photocell',
      'Test all fixtures',
      'Adjust fixture angles',
      'Bury cable',
      'Clean up',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour', quantity: 3 },
      { description: 'Transformer', type: 'material', defaultAmount: 120 },
      { description: 'Cable', type: 'material', defaultAmount: 0 },
      { description: 'Fixtures', type: 'material', defaultAmount: 0 },
    ],
    tags: ['landscape', 'lighting', 'outdoor', 'pathway', 'accent', 'security', 'LED', 'low voltage'],
  },
];

// ============================================================
// GENERAL CONTRACTING  (industryId: 'construction')
// ============================================================

const generalContractingTemplates: JobTemplate[] = [
  {
    id: 'gc-drywall-repair',
    name: 'Drywall Repair',
    description: 'Patch and repair drywall — holes, cracks, water damage.',
    industryId: 'construction',
    estimatedMinutes: 120,
    priority: 'low',
    checklist: [
      'Assess damage extent',
      'Cut out damaged section',
      'Install backing/furring if needed',
      'Cut and fit new drywall',
      'Tape and mud (first coat)',
      'Sand when dry',
      'Second coat of mud',
      'Final sand',
      'Prime',
      'Paint to match (if paint provided)',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 75, unit: 'hour', quantity: 2 },
      { description: 'Drywall materials', type: 'material', defaultAmount: 30 },
      { description: 'Mud and tape', type: 'material', defaultAmount: 15 },
    ],
    tags: ['drywall', 'patch', 'hole', 'repair', 'wall', 'ceiling', 'crack', 'water damage'],
  },
  {
    id: 'gc-door-install',
    name: 'Door Install/Replace',
    description: 'Install or replace interior or exterior door.',
    industryId: 'construction',
    estimatedMinutes: 180,
    priority: 'low',
    checklist: [
      'Remove old door and hardware',
      'Check frame condition',
      'Shim and level new frame (if pre-hung)',
      'Mount hinges',
      'Hang door',
      'Install strike plate',
      'Install handle and lock',
      'Adjust for proper swing',
      'Install trim/casing',
      'Check weatherstrip (exterior)',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 75, unit: 'hour', quantity: 3 },
      { description: 'Shims and fasteners', type: 'material', defaultAmount: 15 },
      { description: 'Caulk', type: 'material', defaultAmount: 8 },
    ],
    tags: ['door', 'install', 'replace', 'interior', 'exterior', 'entry', 'screen', 'prehung'],
  },
  {
    id: 'gc-window-install',
    name: 'Window Install',
    description: 'Remove old window, install replacement or new construction window.',
    industryId: 'construction',
    estimatedMinutes: 180,
    priority: 'medium',
    checklist: [
      'Remove interior trim',
      'Remove old window',
      'Clean and prep opening',
      'Check for rot or damage',
      'Dry-fit new window',
      'Apply flashing tape',
      'Install window and shim level',
      'Insulate gaps',
      'Install interior trim',
      'Caulk exterior',
      'Test operation and locks',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour', quantity: 3 },
      { description: 'Insulation and caulk', type: 'material', defaultAmount: 20 },
      { description: 'Flashing tape', type: 'material', defaultAmount: 15 },
    ],
    tags: ['window', 'install', 'replace', 'vinyl', 'double pane', 'energy efficient'],
  },
  {
    id: 'gc-flooring-install',
    name: 'Flooring Install',
    description: 'Install new flooring — hardwood, laminate, vinyl, or tile.',
    industryId: 'construction',
    estimatedMinutes: 480,
    priority: 'low',
    checklist: [
      'Remove old flooring',
      'Prep subfloor (level, clean, repair)',
      'Acclimate new flooring (if hardwood)',
      'Install vapour barrier/underlayment',
      'Begin layout from reference line',
      'Cut and install flooring',
      'Install transitions and trim',
      'Install baseboards',
      'Final clean',
      'Walkthrough with client',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 75, unit: 'hour' },
      { description: 'Underlayment', type: 'material', defaultAmount: 0.75, unit: 'sqft' },
      { description: 'Transitions and trim', type: 'material', defaultAmount: 0 },
    ],
    tags: ['flooring', 'hardwood', 'laminate', 'vinyl', 'tile', 'LVP', 'install'],
  },
  {
    id: 'gc-deck-build',
    name: 'Deck Build/Repair',
    description: 'Build new deck or repair existing — wood or composite.',
    industryId: 'construction',
    estimatedMinutes: 960,
    priority: 'low',
    checklist: [
      'Review design and obtain permit',
      'Dig footings and pour/install sonotubes',
      'Install posts and beams',
      'Frame joists',
      'Install decking boards',
      'Build stairs',
      'Install railing',
      'Apply finish/stain (if wood)',
      'Final inspection',
      'Cleanup and walkthrough',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 75, unit: 'hour' },
      { description: 'Lumber/composite', type: 'material', defaultAmount: 0 },
      { description: 'Hardware and fasteners', type: 'material', defaultAmount: 200 },
      { description: 'Permit', type: 'flat_rate', defaultAmount: 150 },
    ],
    tags: ['deck', 'build', 'repair', 'patio', 'wood', 'composite', 'trex', 'stairs', 'railing'],
  },
  {
    id: 'gc-fence-install',
    name: 'Fence Install/Repair',
    description: 'Install new fence or repair sections — wood, vinyl, or chain link.',
    industryId: 'construction',
    estimatedMinutes: 480,
    priority: 'low',
    checklist: [
      'Mark property line and locate utilities',
      'Dig post holes',
      'Set posts in concrete',
      'Let concrete cure',
      'Install rails',
      'Install pickets/panels',
      'Install gate and hardware',
      'Stain/paint if needed',
      'Cleanup',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 75, unit: 'hour' },
      { description: 'Posts and concrete', type: 'material', defaultAmount: 0 },
      { description: 'Panels/pickets', type: 'material', defaultAmount: 0 },
      { description: 'Gate hardware', type: 'material', defaultAmount: 45 },
    ],
    tags: ['fence', 'install', 'repair', 'wood', 'vinyl', 'chain link', 'gate', 'privacy'],
  },
  {
    id: 'gc-tile-work',
    name: 'Tile Work',
    description: 'Install tile — backsplash, bathroom floor, shower surround.',
    industryId: 'construction',
    estimatedMinutes: 480,
    priority: 'low',
    checklist: [
      'Prep surface (cement board if needed)',
      'Waterproof membrane (wet areas)',
      'Plan layout and reference lines',
      'Mix thinset',
      'Set tiles with spacers',
      'Let set overnight',
      'Mix and apply grout',
      'Clean excess grout',
      'Apply grout sealer',
      'Caulk transitions',
      'Final clean',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour' },
      { description: 'Thinset and grout', type: 'material', defaultAmount: 50 },
      { description: 'Cement board', type: 'material', defaultAmount: 0 },
      { description: 'Waterproofing', type: 'material', defaultAmount: 0 },
    ],
    tags: ['tile', 'backsplash', 'bathroom', 'shower', 'floor', 'ceramic', 'porcelain'],
  },
  {
    id: 'gc-trim-moulding',
    name: 'Trim/Moulding Install',
    description: 'Install baseboards, crown moulding, or casing.',
    industryId: 'construction',
    estimatedMinutes: 240,
    priority: 'low',
    checklist: [
      'Measure and plan cuts',
      'Cut mitre joints',
      'Install with nail gun',
      'Fill nail holes',
      'Caulk gaps at wall/ceiling',
      'Sand filler',
      'Prime and paint',
      'Cleanup',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 75, unit: 'hour', quantity: 4 },
      { description: 'Wood filler and caulk', type: 'material', defaultAmount: 15 },
      { description: 'Nails', type: 'material', defaultAmount: 10 },
    ],
    tags: ['trim', 'baseboard', 'crown', 'moulding', 'casing', 'install', 'finish carpentry'],
  },
];

// ============================================================
// LANDSCAPING & SNOW REMOVAL  (industryId: 'landscaping')
// ============================================================

const landscapingTemplates: JobTemplate[] = [
  {
    id: 'landscape-lawn-mowing',
    name: 'Lawn Mowing',
    description: 'Regular lawn mowing service — cut, trim, blow.',
    industryId: 'landscaping',
    estimatedMinutes: 45,
    priority: 'low',
    checklist: [
      'Mow lawn (pattern cut)',
      'String trim edges',
      'Edge along walkways and driveways',
      'Blow clippings from hard surfaces',
      'Spot check for weeds or damage',
      'Note any issues to client',
    ],
    lineItems: [
      { description: 'Lawn mowing', type: 'flat_rate', defaultAmount: 55 },
    ],
    tags: ['mow', 'lawn', 'cut', 'grass', 'weekly', 'bi-weekly', 'maintenance'],
  },
  {
    id: 'landscape-spring-cleanup',
    name: 'Spring Cleanup',
    description: 'Full spring cleanup — debris removal, bed prep, first cut.',
    industryId: 'landscaping',
    estimatedMinutes: 180,
    priority: 'medium',
    checklist: [
      'Rake and remove leaves/debris',
      'Clear garden beds',
      'Cut back perennials',
      'Edge all beds',
      'Prune dead branches',
      'First mow of season',
      'Blow all hard surfaces',
      'Haul debris',
      'Inspect irrigation (if applicable)',
    ],
    lineItems: [
      { description: 'Spring cleanup', type: 'flat_rate', defaultAmount: 250 },
      { description: 'Debris disposal', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['spring', 'cleanup', 'debris', 'rake', 'garden', 'seasonal'],
  },
  {
    id: 'landscape-fall-cleanup',
    name: 'Fall Cleanup',
    description: 'Fall cleanup — leaf removal, bed prep, winterizing.',
    industryId: 'landscaping',
    estimatedMinutes: 180,
    priority: 'medium',
    checklist: [
      'Blow/rake all leaves',
      'Clear garden beds',
      'Cut back perennials',
      'Final mow at low height',
      'Apply fall fertilizer (if requested)',
      'Blow out irrigation (if applicable)',
      'Cover tender plants',
      'Haul debris',
    ],
    lineItems: [
      { description: 'Fall cleanup', type: 'flat_rate', defaultAmount: 275 },
      { description: 'Leaf disposal', type: 'flat_rate', defaultAmount: 75 },
    ],
    tags: ['fall', 'cleanup', 'leaves', 'winterize', 'seasonal'],
  },
  {
    id: 'landscape-tree-shrub-trimming',
    name: 'Tree/Shrub Trimming',
    description: 'Trim trees and shrubs — shaping, deadwood removal, clearance.',
    industryId: 'landscaping',
    estimatedMinutes: 120,
    priority: 'low',
    checklist: [
      'Assess trees/shrubs with client',
      'Remove deadwood',
      'Shape and thin as needed',
      'Clear branches from structures',
      'Cut back overgrowth',
      'Chip or bundle branches',
      'Clean up debris',
      'Haul away',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 75, unit: 'hour', quantity: 2 },
      { description: 'Disposal', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['tree', 'shrub', 'trim', 'prune', 'deadwood', 'shape', 'hedge'],
  },
  {
    id: 'landscape-mulch-install',
    name: 'Mulch Install',
    description: 'Deliver and spread mulch in garden beds.',
    industryId: 'landscaping',
    estimatedMinutes: 120,
    priority: 'low',
    checklist: [
      'Edge garden beds',
      'Remove old mulch if needed',
      'Weed beds',
      'Spread mulch to 2-3 inch depth',
      'Keep mulch away from tree trunks',
      'Clean walkways and driveways',
      'Blow off hard surfaces',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 65, unit: 'hour', quantity: 2 },
      { description: 'Mulch per yard', type: 'material', defaultAmount: 55, unit: 'yard' },
      { description: 'Delivery', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['mulch', 'garden bed', 'spread', 'cedar', 'black', 'delivery'],
  },
  {
    id: 'landscape-sod-install',
    name: 'Sod Install',
    description: 'Remove old lawn and install new sod.',
    industryId: 'landscaping',
    estimatedMinutes: 480,
    priority: 'low',
    checklist: [
      'Remove existing grass/weeds',
      'Grade and level soil',
      'Add topsoil if needed',
      'Roll and compact',
      'Lay sod in staggered rows',
      'Roll new sod',
      'Water thoroughly',
      'Advise client on watering schedule',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 65, unit: 'hour' },
      { description: 'Sod', type: 'material', defaultAmount: 0 },
      { description: 'Topsoil', type: 'material', defaultAmount: 0 },
      { description: 'Delivery', type: 'flat_rate', defaultAmount: 75 },
    ],
    tags: ['sod', 'lawn', 'install', 'grass', 'turf', 'new lawn'],
  },
  {
    id: 'landscape-snow-residential',
    name: 'Snow Plowing — Residential',
    description: 'Residential snow clearing — driveway, walkways, porch.',
    industryId: 'landscaping',
    estimatedMinutes: 30,
    priority: 'high',
    checklist: [
      'Plow/blow driveway',
      'Shovel walkways',
      'Clear porch/steps',
      'Apply salt/sand',
      'Check mailbox access',
      'Photo for proof of service',
    ],
    lineItems: [
      { description: 'Residential snow clearing', type: 'flat_rate', defaultAmount: 65 },
      { description: 'Salt application', type: 'flat_rate', defaultAmount: 20 },
    ],
    tags: ['snow', 'plow', 'shovel', 'driveway', 'winter', 'residential', 'salt'],
  },
  {
    id: 'landscape-snow-commercial',
    name: 'Snow Plowing — Commercial',
    description: 'Commercial lot snow clearing and salt/sand application.',
    industryId: 'landscaping',
    estimatedMinutes: 90,
    priority: 'high',
    checklist: [
      'Plow lot',
      'Stack snow in designated areas',
      'Clear fire lanes',
      'Clear accessible parking spots',
      'Salt/sand lot',
      'Clear sidewalks',
      'Salt sidewalks',
      'Photo for proof of service',
      'Log completion time',
    ],
    lineItems: [
      { description: 'Commercial plowing', type: 'flat_rate', defaultAmount: 175 },
      { description: 'Salt/sand', type: 'material', defaultAmount: 2.50, unit: 'kg' },
      { description: 'Sidewalk clearing', type: 'flat_rate', defaultAmount: 75 },
    ],
    tags: ['snow', 'commercial', 'lot', 'parking', 'plow', 'salt', 'sand'],
  },
  {
    id: 'landscape-irrigation',
    name: 'Irrigation Install/Repair',
    description: 'Install new irrigation system or repair existing zones.',
    industryId: 'landscaping',
    estimatedMinutes: 240,
    priority: 'medium',
    checklist: [
      'Plan zones and head placement',
      'Trench lines',
      'Install valve manifold',
      'Run pipe to zones',
      'Install heads',
      'Connect to water supply',
      'Program controller',
      'Test each zone',
      'Adjust head coverage',
      'Mark valve locations',
      'Backfill trenches',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 75, unit: 'hour', quantity: 4 },
      { description: 'Pipe and fittings', type: 'material', defaultAmount: 0 },
      { description: 'Heads', type: 'material', defaultAmount: 0 },
      { description: 'Controller', type: 'material', defaultAmount: 0 },
    ],
    tags: ['irrigation', 'sprinkler', 'install', 'repair', 'zone', 'head', 'controller', 'watering'],
  },
  {
    id: 'landscape-retaining-wall',
    name: 'Retaining Wall',
    description: 'Build retaining wall — block, natural stone, or timber.',
    industryId: 'landscaping',
    estimatedMinutes: 960,
    priority: 'low',
    checklist: [
      'Excavate and level base',
      'Compact soil',
      'Lay gravel base',
      'Level first course',
      'Stack courses with setback',
      'Install drainage (perforated pipe + gravel)',
      'Backfill behind wall',
      'Install cap blocks',
      'Compact backfill',
      'Grade soil',
      'Clean up',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 75, unit: 'hour' },
      { description: 'Wall block', type: 'material', defaultAmount: 0 },
      { description: 'Gravel and drainage', type: 'material', defaultAmount: 0 },
      { description: 'Equipment rental', type: 'flat_rate', defaultAmount: 200 },
    ],
    tags: ['retaining wall', 'wall', 'block', 'stone', 'timber', 'grade', 'slope'],
  },
];

// ============================================================
// CLEANING  (industryId: 'cleaning')
// ============================================================

const cleaningTemplates: JobTemplate[] = [
  {
    id: 'cleaning-standard-residential',
    name: 'Standard Clean — Residential',
    description: 'Regular residential cleaning — kitchen, bathrooms, floors, dusting.',
    industryId: 'cleaning',
    estimatedMinutes: 120,
    priority: 'low',
    checklist: [
      'Kitchen (counters, sink, appliance fronts, floor)',
      'Bathrooms (toilet, tub/shower, sink, mirror, floor)',
      'Dust all surfaces',
      'Vacuum carpets',
      'Mop hard floors',
      'Empty garbage bins',
      'Make beds (if requested)',
      'Wipe light switches and door handles',
    ],
    lineItems: [
      { description: 'Standard clean', type: 'flat_rate', defaultAmount: 150 },
    ],
    tags: ['clean', 'residential', 'standard', 'regular', 'weekly', 'bi-weekly', 'house'],
  },
  {
    id: 'cleaning-deep-residential',
    name: 'Deep Clean — Residential',
    description: 'Detailed deep clean — baseboards, inside appliances, windows.',
    industryId: 'cleaning',
    estimatedMinutes: 240,
    priority: 'low',
    checklist: [
      'Everything in standard clean PLUS:',
      'Inside oven',
      'Inside fridge',
      'Baseboards',
      'Window sills and tracks',
      'Light fixtures',
      'Cabinet fronts',
      'Behind and under furniture',
      'Door frames',
      'Vent covers',
      'Inside microwave',
    ],
    lineItems: [
      { description: 'Deep clean', type: 'flat_rate', defaultAmount: 325 },
    ],
    tags: ['deep clean', 'detailed', 'spring clean', 'thorough', 'residential'],
  },
  {
    id: 'cleaning-move-in-out',
    name: 'Move-In/Move-Out Clean',
    description: 'Thorough cleaning for vacant unit — pre or post tenant.',
    industryId: 'cleaning',
    estimatedMinutes: 300,
    priority: 'medium',
    checklist: [
      'Deep clean all rooms',
      'Inside all closets and cabinets',
      'Inside oven and fridge',
      'All light fixtures',
      'Window sills and blinds',
      'Baseboards',
      'Patch holes (if included)',
      'Inside washer/dryer',
      'Garage sweep (if applicable)',
      'Final walkthrough with photos',
    ],
    lineItems: [
      { description: 'Move-in/out clean', type: 'flat_rate', defaultAmount: 425 },
      { description: 'Add-on: inside windows', type: 'flat_rate', defaultAmount: 75 },
      { description: 'Add-on: garage', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['move in', 'move out', 'vacant', 'tenant', 'turnover', 'empty', 'rental'],
  },
  {
    id: 'cleaning-post-construction',
    name: 'Post-Construction Clean',
    description: 'Clean after renovation or construction — dust, debris, detail.',
    industryId: 'cleaning',
    estimatedMinutes: 480,
    priority: 'medium',
    checklist: [
      'Remove all construction debris',
      'Vacuum all dust from surfaces',
      'Wipe all walls and ceilings',
      'Clean inside cabinets and drawers',
      'Remove window stickers and labels',
      'Clean all fixtures and hardware',
      'Mop all floors',
      'Polish countertops',
      'Final dust pass',
      'Detail window tracks and sills',
    ],
    lineItems: [
      { description: 'Post-construction clean', type: 'flat_rate', defaultAmount: 550 },
      { description: 'Debris removal', type: 'flat_rate', defaultAmount: 100 },
    ],
    tags: ['post construction', 'renovation', 'builder', 'dust', 'debris', 'new build'],
  },
  {
    id: 'cleaning-office-daily',
    name: 'Office Clean — Daily',
    description: 'Daily office cleaning — desks, kitchen, washrooms, floors.',
    industryId: 'cleaning',
    estimatedMinutes: 90,
    priority: 'low',
    checklist: [
      'Empty all bins and replace liners',
      'Wipe desks and surfaces',
      'Clean kitchen (sink, counter, appliances)',
      'Clean washrooms (toilet, sink, mirror, restock)',
      'Vacuum/mop floors',
      'Wipe door handles and light switches',
      'Restock paper products',
    ],
    lineItems: [
      { description: 'Daily office clean', type: 'flat_rate', defaultAmount: 125 },
    ],
    tags: ['office', 'daily', 'commercial', 'workplace', 'corporate'],
  },
  {
    id: 'cleaning-carpet',
    name: 'Carpet Cleaning',
    description: 'Professional carpet cleaning — hot water extraction or dry clean.',
    industryId: 'cleaning',
    estimatedMinutes: 120,
    priority: 'low',
    checklist: [
      'Move light furniture',
      'Pre-treat stains',
      'Hot water extract / steam clean',
      'Edge and corner detailing',
      'Deodorize (if requested)',
      'Set fans for drying',
      'Replace furniture',
      'Advise on dry time',
    ],
    lineItems: [
      { description: 'Carpet cleaning per room', type: 'flat_rate', defaultAmount: 75, unit: 'room' },
      { description: 'Stain treatment', type: 'flat_rate', defaultAmount: 25, unit: 'spot' },
      { description: 'Deodorizing', type: 'flat_rate', defaultAmount: 40 },
    ],
    tags: ['carpet', 'clean', 'steam', 'extraction', 'stain', 'deodorize'],
  },
  {
    id: 'cleaning-window',
    name: 'Window Cleaning',
    description: 'Interior and exterior window cleaning.',
    industryId: 'cleaning',
    estimatedMinutes: 120,
    priority: 'low',
    checklist: [
      'Remove screens',
      'Wash exterior windows',
      'Wash interior windows',
      'Clean window tracks and sills',
      'Clean screens',
      'Replace screens',
      'Wipe frames',
      'Detail corners',
    ],
    lineItems: [
      { description: 'Window cleaning per window', type: 'flat_rate', defaultAmount: 12, unit: 'window' },
      { description: 'Screen cleaning', type: 'flat_rate', defaultAmount: 5, unit: 'screen' },
    ],
    tags: ['window', 'glass', 'clean', 'exterior', 'interior', 'screen'],
  },
  {
    id: 'cleaning-pressure-washing',
    name: 'Pressure Washing',
    description: 'Pressure wash driveway, patio, deck, siding, or walkways.',
    industryId: 'cleaning',
    estimatedMinutes: 180,
    priority: 'low',
    checklist: [
      'Pre-rinse surface',
      'Apply detergent (if needed)',
      'Pressure wash surface',
      'Work in sections',
      'Rinse thoroughly',
      'Detail edges',
      'Check for damage',
      'Clean up equipment',
    ],
    lineItems: [
      { description: 'Pressure washing', type: 'flat_rate', defaultAmount: 0.25, unit: 'sqft' },
      { description: 'Minimum charge', type: 'flat_rate', defaultAmount: 175 },
      { description: 'Detergent', type: 'material', defaultAmount: 20 },
    ],
    tags: ['pressure wash', 'power wash', 'driveway', 'patio', 'deck', 'siding'],
  },
  {
    id: 'cleaning-airbnb-turnover',
    name: 'Airbnb/Rental Turnover',
    description: 'Quick turnover clean between guests — clean, restock, stage.',
    industryId: 'cleaning',
    estimatedMinutes: 90,
    priority: 'high',
    checklist: [
      'Strip and remake beds with fresh linens',
      'Clean bathrooms',
      'Clean kitchen (dishes, counters, appliances)',
      'Vacuum and mop',
      'Restock toiletries',
      'Restock kitchen basics',
      'Check for damage/missing items',
      'Set welcome materials',
      'Take completion photos',
      'Lock up',
    ],
    lineItems: [
      { description: 'Turnover clean', type: 'flat_rate', defaultAmount: 125 },
      { description: 'Laundry service', type: 'flat_rate', defaultAmount: 35 },
      { description: 'Restocking', type: 'material', defaultAmount: 20 },
    ],
    tags: ['airbnb', 'rental', 'turnover', 'guest', 'short-term', 'VRBO', 'hotel'],
  },
];

// ============================================================
// BEAUTY & PERSONAL CARE  (industryId: 'beauty')
// ============================================================

const beautyTemplates: JobTemplate[] = [
  {
    id: 'beauty-walk-in',
    name: 'Walk-In Service',
    description: 'Walk-in client — service to be determined at chair/station.',
    industryId: 'beauty',
    estimatedMinutes: 60,
    priority: 'medium',
    checklist: [
      'Greet and seat client',
      'Consultation (what they want)',
      'Perform service',
      'Sanitize station after',
      'Process payment',
    ],
    lineItems: [
      { description: 'Service', type: 'flat_rate', defaultAmount: 0 },
    ],
    tags: ['walk-in', 'drop-in', 'no appointment'],
  },
  {
    id: 'beauty-nail-repair',
    name: 'Nail Repair',
    description: 'Fix broken, lifted, or damaged nail — gel, acrylic, or natural.',
    industryId: 'beauty',
    estimatedMinutes: 30,
    priority: 'medium',
    checklist: [
      'Assess damage',
      'Remove damaged product (if applicable)',
      'Prep nail',
      'Repair/rebuild',
      'Shape and finish',
      'Apply topcoat',
      'Cure',
    ],
    lineItems: [
      { description: 'Nail repair', type: 'flat_rate', defaultAmount: 25 },
    ],
    tags: ['nail', 'repair', 'broken', 'lifted', 'fix', 'gel', 'acrylic'],
  },
  {
    id: 'beauty-colour-correction',
    name: 'Colour Correction',
    description: 'Fix previous colour service — salon or at-home.',
    industryId: 'beauty',
    estimatedMinutes: 180,
    priority: 'medium',
    checklist: [
      'Consultation and strand test',
      'Assess damage level',
      'Plan correction steps',
      'Apply corrective colour',
      'Process and check',
      'Tone if needed',
      'Condition/treat',
      'Style',
      'Aftercare instructions',
    ],
    lineItems: [
      { description: 'Colour correction', type: 'flat_rate', defaultAmount: 200 },
      { description: 'Additional product', type: 'material', defaultAmount: 0 },
    ],
    tags: ['colour', 'correction', 'fix', 'hair', 'dye', 'bleach', 'tone'],
  },
  {
    id: 'beauty-equipment-maintenance',
    name: 'Equipment Maintenance',
    description: 'Clean, sanitize, and maintain salon equipment.',
    industryId: 'beauty',
    estimatedMinutes: 60,
    priority: 'low',
    checklist: [
      'Sanitize all tools and implements',
      'Clean UV/LED lamps',
      'Service pedicure chairs',
      'Check autoclave/sterilizer',
      'Restock stations',
      'Inventory check',
      'Clean ventilation filters',
    ],
    lineItems: [
      { description: 'Maintenance time', type: 'labour', defaultAmount: 0 },
    ],
    tags: ['maintenance', 'equipment', 'sanitize', 'clean', 'internal'],
  },
];

// ============================================================
// HAIR SALON & BARBERSHOP  (industryId: 'beauty')
// ============================================================

const hairSalonTemplates: JobTemplate[] = [
  {
    id: 'hair-walk-in-haircut',
    name: 'Walk-In Haircut',
    description: 'Walk-in client — haircut service.',
    industryId: 'beauty',
    estimatedMinutes: 30,
    priority: 'medium',
    checklist: [
      'Greet and seat',
      'Consultation (style preference)',
      'Shampoo (if included)',
      'Cut',
      'Style/blow dry',
      'Apply product',
      'Clean station',
    ],
    lineItems: [
      { description: 'Haircut', type: 'flat_rate', defaultAmount: 35 },
    ],
    tags: ['walk-in', 'haircut', 'trim', 'cut', 'drop-in'],
  },
  {
    id: 'hair-colour-correction',
    name: 'Colour Correction (Hair)',
    description: 'Fix colour that went wrong — salon or at-home.',
    industryId: 'beauty',
    estimatedMinutes: 180,
    priority: 'medium',
    checklist: [
      'Consultation and strand test',
      'Assess damage',
      'Plan correction',
      'Apply colour',
      'Process',
      'Tone if needed',
      'Deep condition',
      'Style',
      'Aftercare instructions',
    ],
    lineItems: [
      { description: 'Colour correction', type: 'flat_rate', defaultAmount: 250 },
    ],
    tags: ['colour', 'correction', 'fix', 'dye', 'bleach', 'tone', 'orange', 'brassy'],
  },
  {
    id: 'hair-bridal-event',
    name: 'Bridal/Event Styling',
    description: 'Special occasion hair styling — bridal, prom, event.',
    industryId: 'beauty',
    estimatedMinutes: 90,
    priority: 'medium',
    checklist: [
      'Consultation (reference photos)',
      'Prep hair',
      'Style (updo, curls, etc.)',
      'Set with pins and spray',
      'Final adjustments',
      'Touch-up kit provided',
    ],
    lineItems: [
      { description: 'Event styling', type: 'flat_rate', defaultAmount: 125 },
      { description: 'Trial run', type: 'flat_rate', defaultAmount: 85 },
    ],
    tags: ['bridal', 'wedding', 'prom', 'event', 'updo', 'styling', 'special occasion'],
  },
];

// ============================================================
// PROPERTY MANAGEMENT  (industryId: 'property')
// ============================================================

const propertyTemplates: JobTemplate[] = [
  {
    id: 'property-move-in-inspection',
    name: 'Tenant Move-In Inspection',
    description: 'Document unit condition before tenant takes possession.',
    industryId: 'property',
    estimatedMinutes: 60,
    priority: 'medium',
    checklist: [
      'Photograph every room (walls, floors, ceiling)',
      'Check all appliances',
      'Run all faucets and test drains',
      'Test all outlets',
      'Check all windows open/close/lock',
      'Check all doors',
      'Test smoke and CO detectors',
      'Check HVAC',
      'Note any existing damage',
      'Meter readings',
      'Sign inspection report with tenant',
    ],
    lineItems: [
      { description: 'Move-in inspection', type: 'flat_rate', defaultAmount: 150 },
    ],
    tags: ['move-in', 'inspection', 'tenant', 'condition', 'report', 'photos'],
  },
  {
    id: 'property-move-out-inspection',
    name: 'Tenant Move-Out Inspection',
    description: 'Document unit condition at tenant departure — compare to move-in.',
    industryId: 'property',
    estimatedMinutes: 60,
    priority: 'medium',
    checklist: [
      'Compare to move-in report',
      'Photograph every room',
      'Note any new damage',
      'Check all appliances',
      'Check all fixtures',
      'Assess cleaning needed',
      'Collect keys and fobs',
      'Final meter readings',
      'Calculate any damage deductions',
      'Prepare deposit return documentation',
    ],
    lineItems: [
      { description: 'Move-out inspection', type: 'flat_rate', defaultAmount: 150 },
      { description: 'Damage report', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['move-out', 'inspection', 'tenant', 'damage', 'deposit', 'keys'],
  },
  {
    id: 'property-maintenance-request',
    name: 'Maintenance Request',
    description: 'Respond to tenant maintenance request — diagnose and repair or schedule contractor.',
    industryId: 'property',
    estimatedMinutes: 60,
    priority: 'high',
    checklist: [
      'Review tenant request',
      'Schedule access',
      'Diagnose issue on site',
      'Repair if simple fix',
      'Order parts if needed',
      'Schedule contractor if specialized',
      'Update tenant on timeline',
      'Follow up after completion',
      'Close request',
    ],
    lineItems: [
      { description: 'Site visit', type: 'flat_rate', defaultAmount: 75 },
      { description: 'Labour', type: 'labour', defaultAmount: 65, unit: 'hour' },
      { description: 'Materials', type: 'material', defaultAmount: 0 },
    ],
    tags: ['maintenance', 'repair', 'tenant', 'request', 'fix', 'broken', 'leak'],
  },
  {
    id: 'property-unit-turnover',
    name: 'Unit Turnover',
    description: 'Prepare unit between tenants — clean, repair, paint, inspect.',
    industryId: 'property',
    estimatedMinutes: 480,
    priority: 'high',
    checklist: [
      'Move-out inspection',
      'Patch nail holes and touch up paint',
      'Deep clean entire unit',
      'Clean inside all appliances',
      'Steam clean carpets',
      'Replace furnace filter',
      'Replace toilet seats',
      'Check all smoke/CO detectors',
      'Replace light bulbs',
      'Touch up caulking',
      'Final walkthrough photos',
    ],
    lineItems: [
      { description: 'Turnover coordination', type: 'flat_rate', defaultAmount: 200 },
      { description: 'Cleaning', type: 'flat_rate', defaultAmount: 350 },
      { description: 'Paint touchup', type: 'labour', defaultAmount: 65, unit: 'hour', quantity: 2 },
      { description: 'Materials', type: 'material', defaultAmount: 50 },
    ],
    tags: ['turnover', 'unit', 'between tenants', 'prepare', 'paint', 'clean', 'vacant'],
  },
  {
    id: 'property-fire-safety',
    name: 'Fire Safety Inspection',
    description: 'Annual fire safety inspection — smoke detectors, extinguishers, exits.',
    industryId: 'property',
    estimatedMinutes: 120,
    priority: 'medium',
    checklist: [
      'Test all smoke detectors',
      'Test all CO detectors',
      'Check fire extinguishers (date, pressure)',
      'Inspect exit signs and emergency lighting',
      'Check fire doors close properly',
      'Inspect common area exits',
      'Document findings',
      'Replace expired equipment',
      'File report',
    ],
    lineItems: [
      { description: 'Fire safety inspection', type: 'flat_rate', defaultAmount: 200 },
      { description: 'Detector batteries', type: 'material', defaultAmount: 30 },
      { description: 'Extinguisher recharge', type: 'material', defaultAmount: 45 },
    ],
    tags: ['fire', 'safety', 'inspection', 'smoke', 'detector', 'extinguisher', 'annual'],
  },
  {
    id: 'property-seasonal-hvac',
    name: 'Seasonal HVAC Check',
    description: 'Seasonal furnace or AC inspection for all units.',
    industryId: 'property',
    estimatedMinutes: 30,
    priority: 'low',
    checklist: [
      'Replace air filter',
      'Visual inspect of unit',
      'Check thermostat operation',
      'Listen for unusual sounds',
      'Check venting',
      'Note any issues',
      'Schedule service if needed',
    ],
    lineItems: [
      { description: 'HVAC check per unit', type: 'flat_rate', defaultAmount: 45 },
      { description: 'Filter', type: 'material', defaultAmount: 15 },
    ],
    tags: ['HVAC', 'furnace', 'AC', 'seasonal', 'filter', 'heating', 'cooling', 'inspection'],
  },
  {
    id: 'property-lockout',
    name: 'Lockout/Lock Change',
    description: 'Tenant lockout assistance or lock replacement.',
    industryId: 'property',
    estimatedMinutes: 30,
    priority: 'high',
    checklist: [
      'Verify tenant identity',
      'Provide access / change lock',
      'Test new key',
      'Provide copies to tenant',
      'Update master key log',
      'Update unit file',
    ],
    lineItems: [
      { description: 'Lockout service', type: 'flat_rate', defaultAmount: 75 },
      { description: 'Lock change', type: 'flat_rate', defaultAmount: 125 },
      { description: 'Key copies', type: 'material', defaultAmount: 15 },
    ],
    tags: ['lockout', 'lock', 'key', 'change', 'access', 'tenant', 'emergency'],
  },
];

// ============================================================
// PHOTOGRAPHY & VIDEOGRAPHY  (industryId: 'creative')
// ============================================================

const photographyTemplates: JobTemplate[] = [
  {
    id: 'photo-portrait',
    name: 'Portrait Session',
    description: 'Individual or family portrait session — studio or on-location.',
    industryId: 'creative',
    estimatedMinutes: 90,
    priority: 'low',
    checklist: [
      'Confirm location and time',
      'Set up lighting/backdrops',
      'Review client preferences',
      'Shoot session',
      'Review selects with client on camera',
      'Pack equipment',
      'Edit photos (post-shoot)',
      'Deliver gallery',
    ],
    lineItems: [
      { description: 'Session fee', type: 'flat_rate', defaultAmount: 250 },
      { description: 'Extra hour', type: 'labour', defaultAmount: 125, unit: 'hour' },
      { description: 'Print package', type: 'flat_rate', defaultAmount: 0 },
    ],
    tags: ['portrait', 'family', 'headshot', 'studio', 'outdoor', 'photos'],
  },
  {
    id: 'photo-event',
    name: 'Event Coverage',
    description: 'Photograph event — corporate, party, conference.',
    industryId: 'creative',
    estimatedMinutes: 240,
    priority: 'medium',
    checklist: [
      'Scout venue/review floor plan',
      'Confirm shot list with client',
      'Set up and test gear',
      'Cover event per shot list',
      'Candid and formal shots',
      'Key moments coverage',
      'Pack equipment',
      'Edit selects',
      'Deliver gallery',
    ],
    lineItems: [
      { description: 'Event coverage', type: 'flat_rate', defaultAmount: 500 },
      { description: 'Extra hour', type: 'labour', defaultAmount: 150, unit: 'hour' },
    ],
    tags: ['event', 'corporate', 'party', 'conference', 'gala', 'coverage'],
  },
  {
    id: 'photo-product',
    name: 'Product Photography',
    description: 'Photograph products for e-commerce, catalogue, or social media.',
    industryId: 'creative',
    estimatedMinutes: 180,
    priority: 'low',
    checklist: [
      'Set up lighting and backdrop',
      'Review product list',
      'Style each product',
      'Shoot multiple angles',
      'Shoot lifestyle shots (if requested)',
      'Review with client',
      'Edit and retouch',
      'Deliver files',
    ],
    lineItems: [
      { description: 'Product photography', type: 'flat_rate', defaultAmount: 75, unit: 'product' },
      { description: 'Minimum session fee', type: 'flat_rate', defaultAmount: 300 },
      { description: 'Rush editing', type: 'flat_rate', defaultAmount: 100 },
    ],
    tags: ['product', 'e-commerce', 'catalogue', 'Amazon', 'Shopify', 'flat lay'],
  },
  {
    id: 'photo-real-estate',
    name: 'Real Estate Photography',
    description: 'Photograph property for listing — interior, exterior, aerial.',
    industryId: 'creative',
    estimatedMinutes: 120,
    priority: 'medium',
    checklist: [
      'Confirm access with agent',
      'Exterior shots (front, back, sides)',
      'Interior room-by-room',
      'Kitchen and bathrooms detail',
      'Yard and outdoor spaces',
      'Drone shots (if included)',
      'Edit and colour correct',
      'Deliver to agent/MLS',
    ],
    lineItems: [
      { description: 'Real estate package', type: 'flat_rate', defaultAmount: 275 },
      { description: 'Drone add-on', type: 'flat_rate', defaultAmount: 150 },
      { description: 'Virtual tour', type: 'flat_rate', defaultAmount: 200 },
    ],
    tags: ['real estate', 'listing', 'property', 'MLS', 'drone', 'aerial', 'interior'],
  },
  {
    id: 'video-production',
    name: 'Video Production',
    description: 'Short-form video production — social media, promo, testimonial.',
    industryId: 'creative',
    estimatedMinutes: 240,
    priority: 'medium',
    checklist: [
      'Pre-production call',
      'Script/storyboard review',
      'Set up camera and audio',
      'Shoot footage per plan',
      'B-roll coverage',
      'Interview/talking heads (if applicable)',
      'Review footage on site',
      'Edit and colour grade',
      'Add graphics/music',
      'Deliver final cut',
    ],
    lineItems: [
      { description: 'Half-day video shoot', type: 'flat_rate', defaultAmount: 750 },
      { description: 'Editing', type: 'labour', defaultAmount: 100, unit: 'hour' },
      { description: 'Music licence', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['video', 'promo', 'social media', 'testimonial', 'corporate', 'content'],
  },
];

// ============================================================
// AUTOMOTIVE REPAIR  (industryId: 'automotive')
// ============================================================

const automotiveTemplates: JobTemplate[] = [
  {
    id: 'auto-diagnostic',
    name: 'Diagnostic Scan',
    description: 'Read check engine light / diagnostic trouble codes.',
    industryId: 'automotive',
    estimatedMinutes: 30,
    priority: 'medium',
    checklist: [
      'Connect OBD scanner',
      'Read all trouble codes',
      'Research codes',
      'Visual inspect related components',
      'Clear codes and test drive (if safe)',
      'Report findings to client',
      'Provide repair estimate',
    ],
    lineItems: [
      { description: 'Diagnostic scan', type: 'flat_rate', defaultAmount: 95 },
    ],
    tags: ['check engine', 'diagnostic', 'OBD', 'code', 'scan', 'light', 'CEL', 'MIL'],
  },
  {
    id: 'auto-oil-change',
    name: 'Oil Change',
    description: 'Standard oil and filter change.',
    industryId: 'automotive',
    estimatedMinutes: 30,
    priority: 'low',
    checklist: [
      'Lift vehicle',
      'Drain old oil',
      'Remove and replace filter',
      'Add new oil to spec',
      'Check oil level',
      'Reset oil life monitor',
      'Check tire pressures',
      'Visual safety inspect',
      'Lower vehicle',
    ],
    lineItems: [
      { description: 'Oil change', type: 'flat_rate', defaultAmount: 65 },
      { description: 'Synthetic upgrade', type: 'flat_rate', defaultAmount: 25 },
      { description: 'Filter', type: 'material', defaultAmount: 12 },
    ],
    tags: ['oil', 'change', 'filter', 'lube', 'synthetic', 'conventional', 'maintenance'],
  },
  {
    id: 'auto-brake-replacement',
    name: 'Brake Replacement',
    description: 'Replace brake pads and/or rotors.',
    industryId: 'automotive',
    estimatedMinutes: 120,
    priority: 'high',
    checklist: [
      'Lift vehicle',
      'Remove wheels',
      'Inspect rotors (measure thickness)',
      'Remove old pads',
      'Inspect callipers and hardware',
      'Install new pads',
      'Resurface or replace rotors',
      'Reassemble',
      'Torque wheels',
      'Bed-in brakes with test drive',
    ],
    lineItems: [
      { description: 'Brake job — front', type: 'flat_rate', defaultAmount: 275 },
      { description: 'Brake job — rear', type: 'flat_rate', defaultAmount: 275 },
      { description: 'Rotors per axle', type: 'material', defaultAmount: 0 },
    ],
    tags: ['brakes', 'pads', 'rotors', 'stop', 'squeak', 'grinding', 'front', 'rear'],
  },
  {
    id: 'auto-tire-swap',
    name: 'Tire Change/Swap',
    description: 'Seasonal tire swap or new tire installation.',
    industryId: 'automotive',
    estimatedMinutes: 60,
    priority: 'medium',
    checklist: [
      'Lift vehicle',
      'Remove current tires',
      'Mount and balance new/seasonal tires',
      'Install and torque to spec',
      'Set tire pressures',
      'Reset TPMS (if applicable)',
      'Store old tires (if offered)',
      'Record tread depth',
    ],
    lineItems: [
      { description: 'Tire swap', type: 'flat_rate', defaultAmount: 80 },
      { description: 'Balance per tire', type: 'flat_rate', defaultAmount: 15 },
      { description: 'TPMS reset', type: 'flat_rate', defaultAmount: 25 },
      { description: 'Tire storage seasonal', type: 'flat_rate', defaultAmount: 80 },
    ],
    tags: ['tire', 'swap', 'seasonal', 'winter', 'summer', 'all-season', 'mount', 'balance'],
  },
  {
    id: 'auto-battery-replacement',
    name: 'Battery Replacement',
    description: 'Test and replace vehicle battery.',
    industryId: 'automotive',
    estimatedMinutes: 30,
    priority: 'medium',
    checklist: [
      'Test existing battery',
      'Test alternator output',
      'Remove old battery',
      'Clean terminals',
      'Install new battery',
      'Secure hold-down',
      'Program radio/clock',
      'Test start',
      'Recycle old battery',
    ],
    lineItems: [
      { description: 'Battery install', type: 'labour', defaultAmount: 35 },
      { description: 'Battery', type: 'material', defaultAmount: 0 },
    ],
    tags: ['battery', 'dead', 'no start', 'replace', 'cold', 'terminals'],
  },
  {
    id: 'auto-wheel-alignment',
    name: 'Wheel Alignment',
    description: '4-wheel alignment — adjust camber, caster, toe.',
    industryId: 'automotive',
    estimatedMinutes: 60,
    priority: 'low',
    checklist: [
      'Test drive assessment',
      'Mount alignment heads',
      'Read current alignment',
      'Adjust toe',
      'Adjust camber (if adjustable)',
      'Adjust caster (if adjustable)',
      'Print before/after report',
      'Test drive to verify',
    ],
    lineItems: [
      { description: '4-wheel alignment', type: 'flat_rate', defaultAmount: 120 },
    ],
    tags: ['alignment', 'pulling', 'uneven wear', 'camber', 'toe', 'caster'],
  },
  {
    id: 'auto-ac-recharge',
    name: 'A/C Recharge',
    description: 'Diagnose and recharge vehicle air conditioning.',
    industryId: 'automotive',
    estimatedMinutes: 60,
    priority: 'medium',
    checklist: [
      'Test current system output',
      'Check for leaks (UV dye or electronic)',
      'Evacuate old refrigerant',
      'Vacuum system',
      'Recharge to spec',
      'Add UV dye',
      'Test output temperature',
      'Check compressor operation',
    ],
    lineItems: [
      { description: 'AC recharge', type: 'flat_rate', defaultAmount: 150 },
      { description: 'Refrigerant', type: 'material', defaultAmount: 35 },
      { description: 'Leak detection', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['AC', 'air conditioning', 'hot', 'recharge', 'freon', 'R134a', 'compressor'],
  },
  {
    id: 'auto-exhaust-repair',
    name: 'Exhaust Repair',
    description: 'Repair or replace exhaust components — muffler, pipe, catalytic converter.',
    industryId: 'automotive',
    estimatedMinutes: 120,
    priority: 'medium',
    checklist: [
      'Lift vehicle',
      'Inspect exhaust system',
      'Identify failed component',
      'Cut out damaged section',
      'Weld or clamp new section',
      'Check for leaks',
      'Lower vehicle',
      'Test drive',
      'Verify no exhaust smell',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour', quantity: 2 },
      { description: 'Exhaust parts', type: 'material', defaultAmount: 0 },
      { description: 'Clamps and hangers', type: 'material', defaultAmount: 25 },
    ],
    tags: ['exhaust', 'muffler', 'loud', 'catalytic converter', 'pipe', 'rust', 'leak'],
  },
];

// ============================================================
// PEST CONTROL  (industryId: 'home_services')
// ============================================================

const pestControlTemplates: JobTemplate[] = [
  {
    id: 'pest-interior-treatment',
    name: 'General Pest Treatment — Interior',
    description: 'Treat interior for common pests — ants, spiders, cockroaches, silverfish.',
    industryId: 'home_services',
    estimatedMinutes: 60,
    priority: 'medium',
    checklist: [
      'Interview client on pest type and locations',
      'Inspect all rooms',
      'Identify pest and entry points',
      'Apply treatment (spray, bait, dust)',
      'Treat baseboards and entry points',
      'Check attic/basement if accessible',
      'Place monitors',
      'Advise on prevention',
      'Schedule follow-up if needed',
    ],
    lineItems: [
      { description: 'Interior treatment', type: 'flat_rate', defaultAmount: 175 },
      { description: 'Follow-up treatment', type: 'flat_rate', defaultAmount: 125 },
    ],
    tags: ['pest', 'ants', 'spiders', 'cockroach', 'roach', 'bugs', 'interior', 'treatment'],
  },
  {
    id: 'pest-exterior-perimeter',
    name: 'Exterior Perimeter Treatment',
    description: 'Barrier treatment around building exterior.',
    industryId: 'home_services',
    estimatedMinutes: 45,
    priority: 'low',
    checklist: [
      'Inspect perimeter',
      'Clear debris from foundation',
      'Spray perimeter barrier',
      'Treat window and door frames',
      'Treat weep holes and utility entries',
      'Check eaves and soffits',
      'Place exterior bait stations',
      'Document treatment',
    ],
    lineItems: [
      { description: 'Perimeter treatment', type: 'flat_rate', defaultAmount: 150 },
    ],
    tags: ['perimeter', 'exterior', 'barrier', 'spray', 'foundation', 'prevention'],
  },
  {
    id: 'pest-rodent-control',
    name: 'Rodent Control',
    description: 'Mouse or rat treatment — trapping, exclusion, monitoring.',
    industryId: 'home_services',
    estimatedMinutes: 60,
    priority: 'high',
    checklist: [
      'Inspect for droppings and signs',
      'Identify entry points',
      'Set traps in active areas',
      'Seal entry points (exclusion)',
      'Remove food sources',
      'Place bait stations (exterior only)',
      'Advise on sanitation',
      'Schedule follow-up check',
    ],
    lineItems: [
      { description: 'Rodent treatment', type: 'flat_rate', defaultAmount: 225 },
      { description: 'Exclusion work', type: 'labour', defaultAmount: 85, unit: 'hour' },
      { description: 'Follow-up', type: 'flat_rate', defaultAmount: 100 },
    ],
    tags: ['mouse', 'mice', 'rat', 'rodent', 'droppings', 'chewing', 'exclusion', 'traps'],
  },
  {
    id: 'pest-bed-bug',
    name: 'Bed Bug Treatment',
    description: 'Bed bug inspection and treatment — chemical or heat.',
    industryId: 'home_services',
    estimatedMinutes: 180,
    priority: 'urgent',
    checklist: [
      'Inspect mattress and box spring',
      'Check headboard and furniture',
      'Inspect baseboards and outlets',
      'Identify severity',
      'Apply chemical treatment OR set up heat treatment',
      'Encase mattresses',
      'Treat adjacent rooms',
      'Advise on prep and laundry',
      'Schedule follow-up (14 days)',
      'Second treatment',
    ],
    lineItems: [
      { description: 'Bed bug treatment per room', type: 'flat_rate', defaultAmount: 350 },
      { description: 'Mattress encasement', type: 'material', defaultAmount: 65 },
      { description: 'Follow-up treatment', type: 'flat_rate', defaultAmount: 200 },
    ],
    tags: ['bed bug', 'bite', 'mattress', 'infestation', 'treatment', 'heat'],
  },
  {
    id: 'pest-wasp-nest',
    name: 'Wasp/Hornet Nest Removal',
    description: 'Remove wasp or hornet nest from structure or property.',
    industryId: 'home_services',
    estimatedMinutes: 45,
    priority: 'high',
    checklist: [
      'Locate nest',
      'Assess species and risk',
      'Don protective gear',
      'Apply treatment to nest',
      'Wait for activity to cease',
      'Remove nest',
      'Inspect for secondary nests',
      'Treat entry area',
      'Advise client on prevention',
    ],
    lineItems: [
      { description: 'Nest removal', type: 'flat_rate', defaultAmount: 175 },
      { description: 'Height surcharge', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['wasp', 'hornet', 'nest', 'bee', 'sting', 'removal', 'yellow jacket'],
  },
  {
    id: 'pest-wildlife-removal',
    name: 'Wildlife Removal',
    description: 'Humanely remove wildlife — raccoon, squirrel, skunk, bird.',
    industryId: 'home_services',
    estimatedMinutes: 90,
    priority: 'medium',
    checklist: [
      'Identify animal and location',
      'Assess entry points',
      'Set humane trap',
      'Check local wildlife regulations',
      'Remove animal safely',
      'Seal entry points',
      'Clean affected area',
      'Install one-way door (if applicable)',
      'Schedule follow-up check',
    ],
    lineItems: [
      { description: 'Wildlife removal', type: 'flat_rate', defaultAmount: 300 },
      { description: 'Exclusion work', type: 'labour', defaultAmount: 85, unit: 'hour' },
      { description: 'Follow-up', type: 'flat_rate', defaultAmount: 100 },
    ],
    tags: ['raccoon', 'squirrel', 'skunk', 'bird', 'wildlife', 'animal', 'attic', 'chimney'],
  },
];

// ============================================================
// PAINTING & DECORATING  (industryId: 'home_services')
// ============================================================

const paintingTemplates: JobTemplate[] = [
  {
    id: 'painting-interior-room',
    name: 'Interior Room Paint',
    description: 'Paint a single room — walls and ceiling.',
    industryId: 'home_services',
    estimatedMinutes: 240,
    priority: 'low',
    checklist: [
      'Move/cover furniture',
      'Tape edges and trim',
      'Patch and sand any holes',
      'Prime patches',
      'Cut in ceiling line',
      'Cut in corners and trim',
      'Roll walls (2 coats)',
      'Roll ceiling (if included)',
      'Remove tape',
      'Touch up',
      'Clean up',
      'Move furniture back',
    ],
    lineItems: [
      { description: 'Labour per room', type: 'flat_rate', defaultAmount: 350 },
      { description: 'Paint', type: 'material', defaultAmount: 65, unit: 'gallon' },
      { description: 'Primer', type: 'material', defaultAmount: 35 },
    ],
    tags: ['paint', 'room', 'interior', 'walls', 'ceiling', 'colour', 'repaint'],
  },
  {
    id: 'painting-full-interior',
    name: 'Full Interior Paint',
    description: 'Paint entire interior — all rooms, halls, trim.',
    industryId: 'home_services',
    estimatedMinutes: 2400,
    priority: 'low',
    checklist: [
      'Protect all floors and furniture',
      'Patch all walls',
      'Prime patches and new drywall',
      'Paint ceilings',
      'Cut in all rooms',
      'Roll all rooms (2 coats)',
      'Paint trim and doors (if included)',
      'Paint closets',
      'Remove all tape',
      'Touch up',
      'Final walkthrough',
      'Clean up',
    ],
    lineItems: [
      { description: 'Full interior', type: 'flat_rate', defaultAmount: 0 },
      { description: 'Paint', type: 'material', defaultAmount: 0 },
      { description: 'Primer', type: 'material', defaultAmount: 0 },
    ],
    tags: ['interior', 'full', 'whole house', 'repaint', 'refresh'],
  },
  {
    id: 'painting-exterior',
    name: 'Exterior Paint',
    description: 'Paint building exterior — siding, trim, doors.',
    industryId: 'home_services',
    estimatedMinutes: 2400,
    priority: 'low',
    checklist: [
      'Pressure wash exterior',
      'Scrape loose paint',
      'Sand rough areas',
      'Caulk gaps and joints',
      'Prime bare wood',
      'Paint body (2 coats)',
      'Paint trim',
      'Paint doors',
      'Paint shutters (if applicable)',
      'Clean up',
      'Final walkthrough',
    ],
    lineItems: [
      { description: 'Exterior paint', type: 'flat_rate', defaultAmount: 0 },
      { description: 'Paint', type: 'material', defaultAmount: 0 },
      { description: 'Primer', type: 'material', defaultAmount: 0 },
      { description: 'Equipment rental', type: 'flat_rate', defaultAmount: 150 },
    ],
    tags: ['exterior', 'outside', 'siding', 'trim', 'house', 'building'],
  },
  {
    id: 'painting-cabinets',
    name: 'Cabinet Painting',
    description: 'Paint kitchen or bathroom cabinets.',
    industryId: 'home_services',
    estimatedMinutes: 960,
    priority: 'low',
    checklist: [
      'Remove all doors and hardware',
      'Label everything',
      'Clean and degrease',
      'Sand all surfaces',
      'Prime (2 coats)',
      'Sand between coats',
      'Paint doors (2 coats)',
      'Paint frames',
      'Reinstall hardware',
      'Rehang doors',
      'Adjust hinges',
      'Final touch-up',
    ],
    lineItems: [
      { description: 'Cabinet painting', type: 'flat_rate', defaultAmount: 0 },
      { description: 'Paint and primer', type: 'material', defaultAmount: 0 },
      { description: 'Sandpaper and supplies', type: 'material', defaultAmount: 35 },
    ],
    tags: ['cabinet', 'kitchen', 'bathroom', 'paint', 'refinish', 'doors'],
  },
  {
    id: 'painting-staining-deck',
    name: 'Deck/Fence Staining',
    description: 'Stain or seal deck or fence — prep, apply, protect.',
    industryId: 'home_services',
    estimatedMinutes: 360,
    priority: 'low',
    checklist: [
      'Pressure wash surface',
      'Let dry (24-48 hrs)',
      'Sand rough spots',
      'Apply wood brightener (if needed)',
      'Tape off adjacent surfaces',
      'Apply stain/sealer (2 coats)',
      'Back-brush puddles',
      'Clean up',
      'Advise on cure time',
    ],
    lineItems: [
      { description: 'Labour', type: 'labour', defaultAmount: 75, unit: 'hour' },
      { description: 'Stain/sealer', type: 'material', defaultAmount: 0 },
      { description: 'Supplies', type: 'material', defaultAmount: 25 },
    ],
    tags: ['deck', 'fence', 'stain', 'seal', 'wood', 'refinish', 'weatherproof'],
  },
];

// ============================================================
// PET SERVICES  (industryId: 'pet')
// ============================================================

const petTemplates: JobTemplate[] = [
  {
    id: 'pet-grooming-full',
    name: 'Full Groom',
    description: 'Full grooming service — bath, cut, nails, ears, teeth.',
    industryId: 'pet',
    estimatedMinutes: 90,
    priority: 'low',
    checklist: [
      'Health check (skin, ears, teeth, nails)',
      'De-mat and brush out',
      'Bath with breed-appropriate shampoo',
      'Blow dry and fluff',
      'Haircut/trim to breed standard or client preference',
      'Nail trim and file',
      'Ear cleaning',
      'Teeth brush (if included)',
      'Bandana or bow',
      'Final check and photo',
    ],
    lineItems: [
      { description: 'Full groom', type: 'flat_rate', defaultAmount: 75 },
      { description: 'De-matting surcharge', type: 'flat_rate', defaultAmount: 25 },
    ],
    tags: ['groom', 'bath', 'cut', 'nails', 'dog', 'cat', 'full service'],
  },
  {
    id: 'pet-bath-only',
    name: 'Bath & Tidy',
    description: 'Bath, blow dry, nail trim, ear clean — no haircut.',
    industryId: 'pet',
    estimatedMinutes: 45,
    priority: 'low',
    checklist: [
      'Quick health check',
      'Brush out',
      'Bath',
      'Blow dry',
      'Nail trim',
      'Ear cleaning',
      'Sanitary trim',
      'Spritz and bandana',
    ],
    lineItems: [
      { description: 'Bath and tidy', type: 'flat_rate', defaultAmount: 45 },
    ],
    tags: ['bath', 'wash', 'nails', 'tidy', 'dog', 'no cut'],
  },
  {
    id: 'pet-nail-trim',
    name: 'Nail Trim',
    description: 'Walk-in nail trim and file.',
    industryId: 'pet',
    estimatedMinutes: 15,
    priority: 'low',
    checklist: [
      'Assess nail length',
      'Trim all nails',
      'File smooth',
      'Check for quicking',
      'Treat if needed',
    ],
    lineItems: [
      { description: 'Nail trim', type: 'flat_rate', defaultAmount: 15 },
    ],
    tags: ['nail', 'trim', 'clip', 'walk-in', 'quick'],
  },
  {
    id: 'pet-dog-walking',
    name: 'Dog Walk',
    description: 'Scheduled dog walk — solo or group.',
    industryId: 'pet',
    estimatedMinutes: 30,
    priority: 'low',
    checklist: [
      'Pick up from home',
      'Leash and harness check',
      'Walk route',
      'Water break',
      'Poop pickup',
      'Return home',
      'Fresh water bowl',
      'Send update to owner',
    ],
    lineItems: [
      { description: 'Dog walk (30 min)', type: 'flat_rate', defaultAmount: 20 },
      { description: 'Dog walk (60 min)', type: 'flat_rate', defaultAmount: 35 },
    ],
    tags: ['walk', 'dog', 'exercise', 'daily', 'solo', 'group'],
  },
  {
    id: 'pet-sitting-visit',
    name: 'Pet Sitting Visit',
    description: 'Drop-in pet sitting visit — feed, water, play, meds.',
    industryId: 'pet',
    estimatedMinutes: 30,
    priority: 'medium',
    checklist: [
      'Arrive and check on pet',
      'Fresh food and water',
      'Administer medication (if needed)',
      'Playtime/exercise',
      'Litter box / yard cleanup',
      'Check home (lights, thermostat, mail)',
      'Lock up',
      'Send photo update to owner',
    ],
    lineItems: [
      { description: 'Drop-in visit', type: 'flat_rate', defaultAmount: 25 },
      { description: 'Medication administration', type: 'flat_rate', defaultAmount: 5 },
    ],
    tags: ['pet sitting', 'drop-in', 'visit', 'cat', 'dog', 'feed', 'meds'],
  },
];

// ============================================================
// WELLNESS & FITNESS  (industryId: 'wellness')
// ============================================================

const wellnessTemplates: JobTemplate[] = [
  {
    id: 'wellness-personal-training',
    name: 'Personal Training Session',
    description: 'One-on-one personal training session — gym or in-home.',
    industryId: 'wellness',
    estimatedMinutes: 60,
    priority: 'low',
    checklist: [
      'Review client goals and progress',
      'Warm-up',
      'Workout per program',
      'Form corrections',
      'Cool-down and stretch',
      'Log workout',
      'Update program if needed',
      'Schedule next session',
    ],
    lineItems: [
      { description: 'Personal training session', type: 'flat_rate', defaultAmount: 75 },
    ],
    tags: ['personal training', 'workout', 'fitness', 'gym', 'exercise', 'session'],
  },
  {
    id: 'wellness-fitness-assessment',
    name: 'Fitness Assessment',
    description: 'Initial fitness assessment — measurements, goals, program design.',
    industryId: 'wellness',
    estimatedMinutes: 90,
    priority: 'low',
    checklist: [
      'Health history review',
      'Goals discussion',
      'Body measurements',
      'Movement screening',
      'Cardio baseline test',
      'Strength assessment',
      'Flexibility check',
      'Design initial program',
      'Walk through first workout',
      'Schedule follow-up',
    ],
    lineItems: [
      { description: 'Fitness assessment', type: 'flat_rate', defaultAmount: 100 },
    ],
    tags: ['assessment', 'evaluation', 'new client', 'intake', 'measurements', 'program'],
  },
  {
    id: 'wellness-group-class',
    name: 'Group Class',
    description: 'Group fitness class — yoga, bootcamp, spin, pilates.',
    industryId: 'wellness',
    estimatedMinutes: 60,
    priority: 'low',
    checklist: [
      'Set up equipment/room',
      'Check attendance',
      'Warm-up',
      'Main workout/flow',
      'Cool-down',
      'Clean and reset equipment',
      'Log attendance',
    ],
    lineItems: [
      { description: 'Group class', type: 'flat_rate', defaultAmount: 20, unit: 'person' },
    ],
    tags: ['class', 'group', 'yoga', 'bootcamp', 'spin', 'pilates', 'HIIT'],
  },
  {
    id: 'wellness-nutrition-consult',
    name: 'Nutrition Consultation',
    description: 'Nutrition consultation — meal planning, macros, goals.',
    industryId: 'wellness',
    estimatedMinutes: 60,
    priority: 'low',
    checklist: [
      'Review food diary',
      'Discuss goals and preferences',
      'Assess current nutrition',
      'Calculate macros/calories',
      'Create meal plan',
      'Provide grocery list',
      'Discuss supplements (if applicable)',
      'Schedule follow-up',
    ],
    lineItems: [
      { description: 'Nutrition consultation', type: 'flat_rate', defaultAmount: 85 },
      { description: 'Custom meal plan', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['nutrition', 'diet', 'meal plan', 'macros', 'consultation', 'food'],
  },
];

// ============================================================
// TRANSPORTATION & DELIVERY  (industryId: 'transportation')
// ============================================================

const transportationTemplates: JobTemplate[] = [
  {
    id: 'transport-local-delivery',
    name: 'Local Delivery',
    description: 'Pick up and deliver item(s) within service area.',
    industryId: 'transportation',
    estimatedMinutes: 60,
    priority: 'medium',
    checklist: [
      'Confirm pickup address and time',
      'Confirm delivery address',
      'Pick up item(s)',
      'Verify item condition',
      'Secure in vehicle',
      'Deliver to recipient',
      'Obtain signature/photo proof',
      'Confirm delivery with sender',
    ],
    lineItems: [
      { description: 'Delivery fee', type: 'flat_rate', defaultAmount: 35 },
      { description: 'Per km over 10km', type: 'flat_rate', defaultAmount: 1.50, unit: 'km' },
    ],
    tags: ['delivery', 'courier', 'pickup', 'drop-off', 'local', 'same day'],
  },
  {
    id: 'transport-residential-move',
    name: 'Residential Move',
    description: 'Full residential move — pack, load, transport, unload.',
    industryId: 'transportation',
    estimatedMinutes: 480,
    priority: 'medium',
    checklist: [
      'Walkthrough at origin',
      'Protect floors and doorways',
      'Disassemble furniture (if needed)',
      'Wrap and pad items',
      'Load truck strategically',
      'Secure load',
      'Transport to destination',
      'Protect floors at destination',
      'Unload and place furniture',
      'Reassemble furniture',
      'Final walkthrough with client',
      'Collect payment',
    ],
    lineItems: [
      { description: 'Moving crew (2 movers)', type: 'labour', defaultAmount: 140, unit: 'hour' },
      { description: 'Truck rental', type: 'flat_rate', defaultAmount: 150 },
      { description: 'Packing materials', type: 'material', defaultAmount: 50 },
      { description: 'Fuel surcharge', type: 'flat_rate', defaultAmount: 25 },
    ],
    tags: ['move', 'moving', 'residential', 'apartment', 'house', 'furniture', 'truck'],
  },
  {
    id: 'transport-junk-removal',
    name: 'Junk Removal',
    description: 'Remove and dispose of unwanted items — furniture, appliances, debris.',
    industryId: 'transportation',
    estimatedMinutes: 120,
    priority: 'low',
    checklist: [
      'Assess items to remove',
      'Provide on-site quote',
      'Load items onto truck',
      'Sweep area clean',
      'Sort recyclable vs landfill',
      'Deliver to appropriate facility',
      'Provide disposal receipt',
    ],
    lineItems: [
      { description: 'Junk removal (half load)', type: 'flat_rate', defaultAmount: 250 },
      { description: 'Junk removal (full load)', type: 'flat_rate', defaultAmount: 450 },
      { description: 'Dump fee', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['junk', 'removal', 'disposal', 'furniture', 'appliance', 'debris', 'cleanout'],
  },
  {
    id: 'transport-towing',
    name: 'Towing Service',
    description: 'Tow vehicle from breakdown/accident to shop or destination.',
    industryId: 'transportation',
    estimatedMinutes: 60,
    priority: 'urgent',
    checklist: [
      'Confirm vehicle location',
      'Confirm destination',
      'Assess vehicle condition',
      'Load vehicle onto flatbed / hook up',
      'Secure vehicle',
      'Transport to destination',
      'Unload safely',
      'Obtain signature',
    ],
    lineItems: [
      { description: 'Tow hookup fee', type: 'flat_rate', defaultAmount: 75 },
      { description: 'Per km', type: 'flat_rate', defaultAmount: 3.50, unit: 'km' },
      { description: 'After-hours surcharge', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['tow', 'towing', 'breakdown', 'accident', 'flatbed', 'roadside'],
  },
];

// ============================================================
// EVENTS & HOSPITALITY  (industryId: 'events')
// ============================================================

const eventsTemplates: JobTemplate[] = [
  {
    id: 'events-setup',
    name: 'Event Setup',
    description: 'Set up venue for event — tables, chairs, decor, AV.',
    industryId: 'events',
    estimatedMinutes: 240,
    priority: 'medium',
    checklist: [
      'Review floor plan with client',
      'Unload equipment',
      'Set up tables and chairs',
      'Lay linens and centrepieces',
      'Set up stage/podium (if needed)',
      'Set up AV equipment',
      'Test microphone and speakers',
      'Set up bar/beverage area',
      'Set up signage',
      'Final walkthrough with client',
    ],
    lineItems: [
      { description: 'Setup labour', type: 'labour', defaultAmount: 65, unit: 'hour', quantity: 4 },
      { description: 'Equipment rental', type: 'flat_rate', defaultAmount: 0 },
      { description: 'Delivery', type: 'flat_rate', defaultAmount: 75 },
    ],
    tags: ['event', 'setup', 'venue', 'tables', 'chairs', 'decor', 'AV'],
  },
  {
    id: 'events-teardown',
    name: 'Event Teardown',
    description: 'Break down and clean up after event.',
    industryId: 'events',
    estimatedMinutes: 180,
    priority: 'medium',
    checklist: [
      'Remove all decor and signage',
      'Break down tables and chairs',
      'Pack AV equipment',
      'Collect all rentals',
      'Bag and remove garbage',
      'Sweep/mop venue',
      'Return venue to original state',
      'Load truck',
      'Final venue walkthrough',
    ],
    lineItems: [
      { description: 'Teardown labour', type: 'labour', defaultAmount: 65, unit: 'hour', quantity: 3 },
      { description: 'Cleaning fee', type: 'flat_rate', defaultAmount: 100 },
    ],
    tags: ['event', 'teardown', 'cleanup', 'breakdown', 'venue'],
  },
  {
    id: 'events-catering-service',
    name: 'Catering Service',
    description: 'Prepare, deliver, and serve food for event.',
    industryId: 'events',
    estimatedMinutes: 360,
    priority: 'medium',
    checklist: [
      'Confirm menu and headcount',
      'Prep food (kitchen)',
      'Pack and transport',
      'Set up buffet/stations',
      'Serve guests',
      'Replenish as needed',
      'Break down food stations',
      'Pack leftovers (if requested)',
      'Clean kitchen area',
      'Collect equipment',
    ],
    lineItems: [
      { description: 'Catering per person', type: 'flat_rate', defaultAmount: 35, unit: 'person' },
      { description: 'Staff', type: 'labour', defaultAmount: 25, unit: 'hour' },
      { description: 'Equipment and delivery', type: 'flat_rate', defaultAmount: 150 },
    ],
    tags: ['catering', 'food', 'buffet', 'serve', 'event', 'wedding', 'corporate'],
  },
  {
    id: 'events-dj-entertainment',
    name: 'DJ / Entertainment',
    description: 'DJ services for event — setup, perform, teardown.',
    industryId: 'events',
    estimatedMinutes: 300,
    priority: 'medium',
    checklist: [
      'Confirm playlist and requests with client',
      'Load and transport equipment',
      'Set up speakers, mixer, and lights',
      'Sound check',
      'MC duties (if included)',
      'Perform set',
      'Take requests',
      'Teardown equipment',
      'Load out',
    ],
    lineItems: [
      { description: 'DJ package (4 hours)', type: 'flat_rate', defaultAmount: 600 },
      { description: 'Extra hour', type: 'labour', defaultAmount: 150, unit: 'hour' },
      { description: 'Lighting upgrade', type: 'flat_rate', defaultAmount: 200 },
    ],
    tags: ['DJ', 'music', 'entertainment', 'wedding', 'party', 'event', 'MC'],
  },
  {
    id: 'events-floral-arrangement',
    name: 'Floral Arrangements',
    description: 'Design and deliver floral arrangements for event or venue.',
    industryId: 'events',
    estimatedMinutes: 180,
    priority: 'low',
    checklist: [
      'Confirm arrangement types and quantities',
      'Source flowers',
      'Prepare arrangements',
      'Quality check each piece',
      'Pack for transport',
      'Deliver to venue',
      'Place and adjust on-site',
      'Pickup after event (if rented vessels)',
    ],
    lineItems: [
      { description: 'Centrepiece', type: 'flat_rate', defaultAmount: 65, unit: 'each' },
      { description: 'Bridal bouquet', type: 'flat_rate', defaultAmount: 150 },
      { description: 'Delivery and setup', type: 'flat_rate', defaultAmount: 75 },
    ],
    tags: ['flowers', 'floral', 'arrangement', 'wedding', 'centrepiece', 'bouquet'],
  },
];

// ============================================================
// TECHNOLOGY & IT  (industryId: 'technology')
// ============================================================

const technologyTemplates: JobTemplate[] = [
  {
    id: 'tech-computer-repair',
    name: 'Computer Repair',
    description: 'Diagnose and repair desktop or laptop — hardware or software.',
    industryId: 'technology',
    estimatedMinutes: 90,
    priority: 'medium',
    checklist: [
      'Interview client on issue',
      'Run diagnostics',
      'Identify hardware or software fault',
      'Backup data (if at risk)',
      'Perform repair or replacement',
      'Test system',
      'Update OS and drivers',
      'Clean up temp files',
      'Verify issue resolved with client',
    ],
    lineItems: [
      { description: 'Diagnostic fee', type: 'flat_rate', defaultAmount: 75 },
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour', quantity: 1 },
      { description: 'Parts', type: 'material', defaultAmount: 0 },
    ],
    tags: ['computer', 'repair', 'laptop', 'desktop', 'slow', 'broken', 'virus', 'hardware'],
  },
  {
    id: 'tech-network-setup',
    name: 'Network Setup',
    description: 'Set up or troubleshoot WiFi, router, switches, or cabling.',
    industryId: 'technology',
    estimatedMinutes: 120,
    priority: 'medium',
    checklist: [
      'Assess current network layout',
      'Plan network topology',
      'Install router/switches',
      'Run and terminate cables (if needed)',
      'Configure WiFi (SSID, security)',
      'Set up guest network',
      'Test speed at all locations',
      'Configure firewall rules',
      'Document network layout and credentials',
      'Walk client through basics',
    ],
    lineItems: [
      { description: 'Network setup', type: 'flat_rate', defaultAmount: 200 },
      { description: 'Labour', type: 'labour', defaultAmount: 85, unit: 'hour' },
      { description: 'Cable and hardware', type: 'material', defaultAmount: 0 },
    ],
    tags: ['network', 'WiFi', 'router', 'switch', 'cable', 'internet', 'setup', 'slow'],
  },
  {
    id: 'tech-security-camera',
    name: 'Security Camera Install',
    description: 'Install and configure security cameras — indoor or outdoor.',
    industryId: 'technology',
    estimatedMinutes: 180,
    priority: 'medium',
    checklist: [
      'Plan camera locations with client',
      'Mount cameras',
      'Run cable (or configure WiFi)',
      'Connect to NVR/recorder',
      'Configure recording schedule',
      'Set up motion detection zones',
      'Set up remote viewing app',
      'Test all camera angles',
      'Walk client through app',
      'Provide login credentials',
    ],
    lineItems: [
      { description: 'Installation labour', type: 'labour', defaultAmount: 85, unit: 'hour', quantity: 3 },
      { description: 'Camera', type: 'material', defaultAmount: 0, unit: 'each' },
      { description: 'NVR/recorder', type: 'material', defaultAmount: 0 },
      { description: 'Cable and mounts', type: 'material', defaultAmount: 50 },
    ],
    tags: ['security', 'camera', 'CCTV', 'NVR', 'surveillance', 'outdoor', 'indoor'],
  },
  {
    id: 'tech-data-backup',
    name: 'Data Backup & Recovery',
    description: 'Set up backup system or recover data from failed drive.',
    industryId: 'technology',
    estimatedMinutes: 120,
    priority: 'high',
    checklist: [
      'Assess current backup situation',
      'Identify data to protect',
      'Set up local backup (external drive / NAS)',
      'Set up cloud backup (if requested)',
      'Configure automatic schedule',
      'Verify first backup completed',
      'Test restore from backup',
      'Document backup procedures',
      'Walk client through restore process',
    ],
    lineItems: [
      { description: 'Backup setup', type: 'flat_rate', defaultAmount: 150 },
      { description: 'Data recovery (per drive)', type: 'flat_rate', defaultAmount: 200 },
      { description: 'External drive', type: 'material', defaultAmount: 0 },
    ],
    tags: ['backup', 'data', 'recovery', 'drive', 'cloud', 'NAS', 'restore', 'lost'],
  },
  {
    id: 'tech-av-install',
    name: 'AV Installation',
    description: 'Install TV mount, sound system, or projector.',
    industryId: 'technology',
    estimatedMinutes: 120,
    priority: 'low',
    checklist: [
      'Find studs and plan mount location',
      'Install mount/bracket',
      'Mount TV/projector',
      'Run and conceal cables',
      'Connect all sources (cable, streaming, gaming)',
      'Set up sound system/soundbar',
      'Configure remote/app',
      'Test all inputs',
      'Clean up',
      'Walk client through operation',
    ],
    lineItems: [
      { description: 'TV mount installation', type: 'flat_rate', defaultAmount: 150 },
      { description: 'Sound system setup', type: 'flat_rate', defaultAmount: 100 },
      { description: 'Cable concealment', type: 'flat_rate', defaultAmount: 75 },
      { description: 'Hardware', type: 'material', defaultAmount: 0 },
    ],
    tags: ['TV', 'mount', 'soundbar', 'projector', 'AV', 'home theatre', 'install'],
  },
];

// ============================================================
// EDUCATION & LESSONS  (industryId: 'education')
// ============================================================

const educationTemplates: JobTemplate[] = [
  {
    id: 'education-tutoring-session',
    name: 'Tutoring Session',
    description: 'One-on-one tutoring session — in-person or virtual.',
    industryId: 'education',
    estimatedMinutes: 60,
    priority: 'low',
    checklist: [
      'Review previous session notes',
      'Check homework/assignments',
      'Identify focus areas',
      'Teach new concepts',
      'Practice problems together',
      'Assign homework',
      'Log session notes',
      'Update parent/student on progress',
    ],
    lineItems: [
      { description: 'Tutoring session (1 hr)', type: 'flat_rate', defaultAmount: 50 },
    ],
    tags: ['tutoring', 'lesson', 'math', 'science', 'english', 'homework', 'student'],
  },
  {
    id: 'education-music-lesson',
    name: 'Music Lesson',
    description: 'Individual music lesson — instrument or voice.',
    industryId: 'education',
    estimatedMinutes: 30,
    priority: 'low',
    checklist: [
      'Warm-up exercises',
      'Review last week\'s practice',
      'Introduce new technique/piece',
      'Guided practice',
      'Assign practice material',
      'Schedule next lesson',
    ],
    lineItems: [
      { description: 'Music lesson (30 min)', type: 'flat_rate', defaultAmount: 35 },
      { description: 'Music lesson (60 min)', type: 'flat_rate', defaultAmount: 60 },
    ],
    tags: ['music', 'lesson', 'piano', 'guitar', 'voice', 'drums', 'instrument'],
  },
  {
    id: 'education-driving-lesson',
    name: 'Driving Lesson',
    description: 'In-car driving lesson — beginner to road test prep.',
    industryId: 'education',
    estimatedMinutes: 90,
    priority: 'low',
    checklist: [
      'Pick up student',
      'Pre-drive briefing',
      'Practice skills (parking, turns, highway)',
      'Correct techniques in real-time',
      'Post-drive debrief',
      'Log hours for student',
      'Drop off student',
      'Update progress notes',
    ],
    lineItems: [
      { description: 'Driving lesson (90 min)', type: 'flat_rate', defaultAmount: 55 },
      { description: 'Road test prep package', type: 'flat_rate', defaultAmount: 200 },
    ],
    tags: ['driving', 'lesson', 'G1', 'G2', 'road test', 'beginner', 'parking'],
  },
  {
    id: 'education-group-workshop',
    name: 'Group Workshop',
    description: 'Group workshop or class — art, cooking, dance, etc.',
    industryId: 'education',
    estimatedMinutes: 120,
    priority: 'low',
    checklist: [
      'Prepare materials and supplies',
      'Set up workspace',
      'Check attendance and collect payment',
      'Introduction and overview',
      'Guided instruction',
      'Hands-on practice',
      'Q&A',
      'Clean up',
      'Send follow-up materials',
    ],
    lineItems: [
      { description: 'Workshop fee per person', type: 'flat_rate', defaultAmount: 45, unit: 'person' },
      { description: 'Materials per person', type: 'material', defaultAmount: 15, unit: 'person' },
    ],
    tags: ['workshop', 'class', 'group', 'art', 'cooking', 'dance', 'craft'],
  },
];

// ============================================================
// HEALTH & MEDICAL  (industryId: 'health')
// ============================================================

const healthTemplates: JobTemplate[] = [
  {
    id: 'health-patient-visit',
    name: 'Patient Visit',
    description: 'Standard patient appointment — consultation, treatment, follow-up.',
    industryId: 'health',
    estimatedMinutes: 30,
    priority: 'medium',
    checklist: [
      'Review patient chart',
      'Greet and verify patient',
      'Discuss symptoms/concerns',
      'Examination',
      'Diagnosis/treatment plan',
      'Prescribe medication (if needed)',
      'Book follow-up (if needed)',
      'Update patient chart',
    ],
    lineItems: [
      { description: 'Office visit', type: 'flat_rate', defaultAmount: 0 },
    ],
    tags: ['patient', 'visit', 'appointment', 'consultation', 'exam', 'follow-up'],
  },
  {
    id: 'health-physio-session',
    name: 'Physiotherapy Session',
    description: 'Physiotherapy treatment — assessment, manual therapy, exercise.',
    industryId: 'health',
    estimatedMinutes: 45,
    priority: 'low',
    checklist: [
      'Review progress since last visit',
      'Reassess range of motion / pain',
      'Manual therapy',
      'Guided exercises',
      'Modalities (ultrasound, TENS, etc.)',
      'Update home exercise program',
      'Book next session',
      'Update chart',
    ],
    lineItems: [
      { description: 'Physiotherapy session', type: 'flat_rate', defaultAmount: 90 },
      { description: 'Initial assessment', type: 'flat_rate', defaultAmount: 120 },
    ],
    tags: ['physio', 'physiotherapy', 'rehab', 'exercise', 'manual therapy', 'injury'],
  },
  {
    id: 'health-chiro-adjustment',
    name: 'Chiropractic Adjustment',
    description: 'Chiropractic consultation and adjustment.',
    industryId: 'health',
    estimatedMinutes: 30,
    priority: 'low',
    checklist: [
      'Review symptoms and progress',
      'Postural assessment',
      'Spinal adjustment',
      'Soft tissue work (if needed)',
      'Stretch and mobilize',
      'Home care instructions',
      'Book next visit',
      'Update chart',
    ],
    lineItems: [
      { description: 'Chiropractic visit', type: 'flat_rate', defaultAmount: 65 },
      { description: 'Initial consultation', type: 'flat_rate', defaultAmount: 100 },
    ],
    tags: ['chiropractic', 'adjustment', 'spine', 'back', 'neck', 'posture'],
  },
  {
    id: 'health-pharmacy-delivery',
    name: 'Pharmacy Delivery',
    description: 'Prepare and deliver prescription medication to patient.',
    industryId: 'health',
    estimatedMinutes: 30,
    priority: 'high',
    checklist: [
      'Verify prescription',
      'Fill medication',
      'Pharmacist check',
      'Package for delivery',
      'Confirm delivery address',
      'Deliver to patient',
      'Verify patient identity',
      'Collect signature',
      'Provide counselling (if needed)',
    ],
    lineItems: [
      { description: 'Delivery fee', type: 'flat_rate', defaultAmount: 5 },
    ],
    tags: ['pharmacy', 'delivery', 'prescription', 'medication', 'meds', 'rx'],
  },
  {
    id: 'health-dental-cleaning',
    name: 'Dental Cleaning',
    description: 'Routine dental cleaning and check-up.',
    industryId: 'health',
    estimatedMinutes: 60,
    priority: 'low',
    checklist: [
      'Review patient history',
      'X-rays (if due)',
      'Scale and polish',
      'Floss',
      'Fluoride treatment (if applicable)',
      'Dentist examination',
      'Discuss findings',
      'Book next appointment',
      'Update chart',
    ],
    lineItems: [
      { description: 'Dental cleaning', type: 'flat_rate', defaultAmount: 200 },
      { description: 'X-rays', type: 'flat_rate', defaultAmount: 75 },
    ],
    tags: ['dental', 'cleaning', 'check-up', 'teeth', 'hygiene', 'polish', 'x-ray'],
  },
];

// ============================================================
// CARE & ERRANDS  (industryId: 'care')
// ============================================================

const careTemplates: JobTemplate[] = [
  {
    id: 'care-senior-visit',
    name: 'Senior Care Visit',
    description: 'In-home senior care — companionship, meals, light housekeeping.',
    industryId: 'care',
    estimatedMinutes: 180,
    priority: 'medium',
    checklist: [
      'Arrive and check on client',
      'Medication reminder/assist',
      'Prepare meal',
      'Light housekeeping',
      'Companionship activities',
      'Accompany on walk (if able)',
      'Grocery list / errands note',
      'Update family/care log',
      'Secure home on departure',
    ],
    lineItems: [
      { description: 'Care visit (3 hrs)', type: 'flat_rate', defaultAmount: 90 },
      { description: 'Extra hour', type: 'labour', defaultAmount: 30, unit: 'hour' },
    ],
    tags: ['senior', 'care', 'elderly', 'companion', 'home care', 'meal', 'medication'],
  },
  {
    id: 'care-childcare-session',
    name: 'Childcare Session',
    description: 'In-home childcare — babysitting, after-school, activities.',
    industryId: 'care',
    estimatedMinutes: 240,
    priority: 'medium',
    checklist: [
      'Arrive and greet family',
      'Review schedule and instructions',
      'Meals/snacks',
      'Activities and play',
      'Homework help (if applicable)',
      'Bedtime routine (if applicable)',
      'Tidy play areas',
      'Update parent on day',
    ],
    lineItems: [
      { description: 'Childcare', type: 'labour', defaultAmount: 20, unit: 'hour' },
      { description: 'Additional child', type: 'labour', defaultAmount: 5, unit: 'hour' },
    ],
    tags: ['childcare', 'babysitting', 'nanny', 'kids', 'after-school', 'care'],
  },
  {
    id: 'care-errand-run',
    name: 'Errand Run',
    description: 'Run errands for client — groceries, pharmacy, post office, etc.',
    industryId: 'care',
    estimatedMinutes: 90,
    priority: 'low',
    checklist: [
      'Confirm errand list with client',
      'Collect payment method / cash',
      'Complete errands in order',
      'Keep receipts',
      'Deliver to client',
      'Return change and receipts',
      'Confirm all items received',
    ],
    lineItems: [
      { description: 'Errand run (up to 2 hrs)', type: 'flat_rate', defaultAmount: 45 },
      { description: 'Mileage', type: 'flat_rate', defaultAmount: 0.55, unit: 'km' },
    ],
    tags: ['errand', 'grocery', 'pharmacy', 'shopping', 'pickup', 'delivery'],
  },
  {
    id: 'care-companion-transport',
    name: 'Companion Transport',
    description: 'Drive client to appointment, errands, or social outing.',
    industryId: 'care',
    estimatedMinutes: 120,
    priority: 'medium',
    checklist: [
      'Arrive at client home',
      'Assist to vehicle (if needed)',
      'Drive to destination',
      'Wait or return at agreed time',
      'Accompany during appointment (if requested)',
      'Drive home',
      'Assist inside (if needed)',
      'Log trip details',
    ],
    lineItems: [
      { description: 'Companion transport', type: 'flat_rate', defaultAmount: 40, unit: 'hour' },
      { description: 'Mileage', type: 'flat_rate', defaultAmount: 0.55, unit: 'km' },
    ],
    tags: ['transport', 'drive', 'appointment', 'companion', 'senior', 'medical'],
  },
];

// ============================================================
// UNION EXPORT
// ============================================================

export const JOB_TEMPLATES: JobTemplate[] = [
  ...plumbingHvacTemplates,
  ...electricalTemplates,
  ...generalContractingTemplates,
  ...landscapingTemplates,
  ...cleaningTemplates,
  ...beautyTemplates,
  ...hairSalonTemplates,
  ...propertyTemplates,
  ...photographyTemplates,
  ...automotiveTemplates,
  ...pestControlTemplates,
  ...paintingTemplates,
  ...petTemplates,
  ...wellnessTemplates,
  ...transportationTemplates,
  ...eventsTemplates,
  ...technologyTemplates,
  ...educationTemplates,
  ...healthTemplates,
  ...careTemplates,
];

// ============================================================
// HELPERS
// ============================================================

/** Get templates for a specific industry category */
export function getJobTemplatesForIndustry(categoryId: string): JobTemplate[] {
  return JOB_TEMPLATES.filter(t => t.industryId === categoryId);
}

/** Get all template names for fuzzy search (e.g. Fuse.js) */
export function getAllJobTemplateNames(): { id: string; name: string; industry: string }[] {
  return JOB_TEMPLATES.map(t => ({ id: t.id, name: t.name, industry: t.industryId }));
}
